/**
 * Pre-Seeded Policy Routing Engine (PRE) Rule Catalog
 *
 * Authoritative typed catalog of all production PRE rules derived from the
 * Policy Routing Engine specification and Telecom Laws document. Each rule
 * carries its source statute citation in `sourceRef` and is upserted into
 * the database by services/api-gateway/src/seeds/pre-seed.ts.
 *
 * The `PRE_*` arrays below are PRODUCTION data; the super-admin UI also reads
 * them directly so operators see the same catalog whether they're browsing
 * the policy library or auditing what's seeded in the DB.
 *
 * The `MOCK_DECISION_LOGS` and `MOCK_AI_CLASSIFICATIONS` arrays at the bottom
 * of this file ARE genuine UI placeholders — they let the super-admin pages
 * render example rows before real decisions / classifications exist.
 */
import type {
  Jurisdiction,
  Rule,
  CallingHourWindow,
  DNCRegistry,
  FrequencyCapConfig,
  PREDecisionLog,
  PREAiClassification,
  PRECheckDefinition,
} from '@respectly/shared-types';

// ============================================
// Jurisdictions (15 entries)
// ============================================

export const PRE_JURISDICTIONS: Jurisdiction[] = [
  {
    id: '10000000-0000-0000-0000-000000000001',
    code: 'GLOBAL',
    name: 'Global Baseline',
    country: 'ALL',
    subdivisionType: 'GLOBAL',
    parentCode: null,
    isActive: true,
    isSystem: true,
    createdAt: new Date('2024-01-01'),
    updatedAt: new Date('2024-01-01'),
  },
  {
    id: '10000000-0000-0000-0000-000000000002',
    code: 'US-FED',
    name: 'United States (Federal)',
    country: 'US',
    subdivisionType: 'FEDERAL',
    parentCode: 'GLOBAL',
    isActive: true,
    isSystem: true,
    createdAt: new Date('2024-01-01'),
    updatedAt: new Date('2024-01-01'),
  },
  {
    id: '10000000-0000-0000-0000-000000000003',
    code: 'US-FL',
    name: 'Florida (FTSA)',
    country: 'US',
    subdivisionType: 'STATE',
    parentCode: 'US-FED',
    isActive: true,
    isSystem: true,
    createdAt: new Date('2024-01-01'),
    updatedAt: new Date('2024-01-01'),
  },
  {
    id: '10000000-0000-0000-0000-000000000004',
    code: 'US-OK',
    name: 'Oklahoma',
    country: 'US',
    subdivisionType: 'STATE',
    parentCode: 'US-FED',
    isActive: true,
    isSystem: true,
    createdAt: new Date('2024-01-01'),
    updatedAt: new Date('2024-01-01'),
  },
  {
    id: '10000000-0000-0000-0000-000000000005',
    code: 'US-WA',
    name: 'Washington (CSTA)',
    country: 'US',
    subdivisionType: 'STATE',
    parentCode: 'US-FED',
    isActive: true,
    isSystem: true,
    createdAt: new Date('2024-01-01'),
    updatedAt: new Date('2024-01-01'),
  },
  {
    id: '10000000-0000-0000-0000-000000000006',
    code: 'US-MD',
    name: 'Maryland',
    country: 'US',
    subdivisionType: 'STATE',
    parentCode: 'US-FED',
    isActive: true,
    isSystem: true,
    createdAt: new Date('2024-01-01'),
    updatedAt: new Date('2024-01-01'),
  },
  {
    id: '10000000-0000-0000-0000-000000000007',
    code: 'US-NY',
    name: 'New York',
    country: 'US',
    subdivisionType: 'STATE',
    parentCode: 'US-FED',
    isActive: true,
    isSystem: true,
    createdAt: new Date('2024-01-01'),
    updatedAt: new Date('2024-01-01'),
  },
  {
    id: '10000000-0000-0000-0000-000000000008',
    code: 'US-AZ',
    name: 'Arizona',
    country: 'US',
    subdivisionType: 'STATE',
    parentCode: 'US-FED',
    isActive: true,
    isSystem: true,
    createdAt: new Date('2024-01-01'),
    updatedAt: new Date('2024-01-01'),
  },
  // ---------------------------------------------------------------------------
  // Remaining US states + DC. Jurisdiction rows only — these inherit US-FED
  // rules via parentCode. State-specific PRERule rows are added separately
  // for states with a verified telemarketing/mini-TCPA statute (see legal team
  // citations); states without statute-level rules rely on US-FED inheritance.
  // ---------------------------------------------------------------------------
  { id: '10000000-0000-0000-0000-0000000000a1', code: 'US-AL', name: 'Alabama', country: 'US', subdivisionType: 'STATE', parentCode: 'US-FED', isActive: true, isSystem: true, createdAt: new Date('2024-01-01'), updatedAt: new Date('2024-01-01') },
  { id: '10000000-0000-0000-0000-0000000000a2', code: 'US-AK', name: 'Alaska', country: 'US', subdivisionType: 'STATE', parentCode: 'US-FED', isActive: true, isSystem: true, createdAt: new Date('2024-01-01'), updatedAt: new Date('2024-01-01') },
  { id: '10000000-0000-0000-0000-0000000000a3', code: 'US-AR', name: 'Arkansas', country: 'US', subdivisionType: 'STATE', parentCode: 'US-FED', isActive: true, isSystem: true, createdAt: new Date('2024-01-01'), updatedAt: new Date('2024-01-01') },
  { id: '10000000-0000-0000-0000-0000000000a4', code: 'US-CA', name: 'California', country: 'US', subdivisionType: 'STATE', parentCode: 'US-FED', isActive: true, isSystem: true, createdAt: new Date('2024-01-01'), updatedAt: new Date('2024-01-01') },
  { id: '10000000-0000-0000-0000-0000000000a5', code: 'US-CO', name: 'Colorado', country: 'US', subdivisionType: 'STATE', parentCode: 'US-FED', isActive: true, isSystem: true, createdAt: new Date('2024-01-01'), updatedAt: new Date('2024-01-01') },
  { id: '10000000-0000-0000-0000-0000000000a6', code: 'US-CT', name: 'Connecticut', country: 'US', subdivisionType: 'STATE', parentCode: 'US-FED', isActive: true, isSystem: true, createdAt: new Date('2024-01-01'), updatedAt: new Date('2024-01-01') },
  { id: '10000000-0000-0000-0000-0000000000a7', code: 'US-DE', name: 'Delaware', country: 'US', subdivisionType: 'STATE', parentCode: 'US-FED', isActive: true, isSystem: true, createdAt: new Date('2024-01-01'), updatedAt: new Date('2024-01-01') },
  { id: '10000000-0000-0000-0000-0000000000a8', code: 'US-DC', name: 'District of Columbia', country: 'US', subdivisionType: 'STATE', parentCode: 'US-FED', isActive: true, isSystem: true, createdAt: new Date('2024-01-01'), updatedAt: new Date('2024-01-01') },
  { id: '10000000-0000-0000-0000-0000000000a9', code: 'US-GA', name: 'Georgia', country: 'US', subdivisionType: 'STATE', parentCode: 'US-FED', isActive: true, isSystem: true, createdAt: new Date('2024-01-01'), updatedAt: new Date('2024-01-01') },
  { id: '10000000-0000-0000-0000-0000000000aa', code: 'US-HI', name: 'Hawaii', country: 'US', subdivisionType: 'STATE', parentCode: 'US-FED', isActive: true, isSystem: true, createdAt: new Date('2024-01-01'), updatedAt: new Date('2024-01-01') },
  { id: '10000000-0000-0000-0000-0000000000ab', code: 'US-ID', name: 'Idaho', country: 'US', subdivisionType: 'STATE', parentCode: 'US-FED', isActive: true, isSystem: true, createdAt: new Date('2024-01-01'), updatedAt: new Date('2024-01-01') },
  { id: '10000000-0000-0000-0000-0000000000ac', code: 'US-IL', name: 'Illinois', country: 'US', subdivisionType: 'STATE', parentCode: 'US-FED', isActive: true, isSystem: true, createdAt: new Date('2024-01-01'), updatedAt: new Date('2024-01-01') },
  { id: '10000000-0000-0000-0000-0000000000ad', code: 'US-IN', name: 'Indiana', country: 'US', subdivisionType: 'STATE', parentCode: 'US-FED', isActive: true, isSystem: true, createdAt: new Date('2024-01-01'), updatedAt: new Date('2024-01-01') },
  { id: '10000000-0000-0000-0000-0000000000ae', code: 'US-IA', name: 'Iowa', country: 'US', subdivisionType: 'STATE', parentCode: 'US-FED', isActive: true, isSystem: true, createdAt: new Date('2024-01-01'), updatedAt: new Date('2024-01-01') },
  { id: '10000000-0000-0000-0000-0000000000af', code: 'US-KS', name: 'Kansas', country: 'US', subdivisionType: 'STATE', parentCode: 'US-FED', isActive: true, isSystem: true, createdAt: new Date('2024-01-01'), updatedAt: new Date('2024-01-01') },
  { id: '10000000-0000-0000-0000-0000000000b1', code: 'US-KY', name: 'Kentucky', country: 'US', subdivisionType: 'STATE', parentCode: 'US-FED', isActive: true, isSystem: true, createdAt: new Date('2024-01-01'), updatedAt: new Date('2024-01-01') },
  { id: '10000000-0000-0000-0000-0000000000b2', code: 'US-LA', name: 'Louisiana', country: 'US', subdivisionType: 'STATE', parentCode: 'US-FED', isActive: true, isSystem: true, createdAt: new Date('2024-01-01'), updatedAt: new Date('2024-01-01') },
  { id: '10000000-0000-0000-0000-0000000000b3', code: 'US-ME', name: 'Maine', country: 'US', subdivisionType: 'STATE', parentCode: 'US-FED', isActive: true, isSystem: true, createdAt: new Date('2024-01-01'), updatedAt: new Date('2024-01-01') },
  { id: '10000000-0000-0000-0000-0000000000b4', code: 'US-MA', name: 'Massachusetts', country: 'US', subdivisionType: 'STATE', parentCode: 'US-FED', isActive: true, isSystem: true, createdAt: new Date('2024-01-01'), updatedAt: new Date('2024-01-01') },
  { id: '10000000-0000-0000-0000-0000000000b5', code: 'US-MI', name: 'Michigan', country: 'US', subdivisionType: 'STATE', parentCode: 'US-FED', isActive: true, isSystem: true, createdAt: new Date('2024-01-01'), updatedAt: new Date('2024-01-01') },
  { id: '10000000-0000-0000-0000-0000000000b6', code: 'US-MN', name: 'Minnesota', country: 'US', subdivisionType: 'STATE', parentCode: 'US-FED', isActive: true, isSystem: true, createdAt: new Date('2024-01-01'), updatedAt: new Date('2024-01-01') },
  { id: '10000000-0000-0000-0000-0000000000b7', code: 'US-MS', name: 'Mississippi', country: 'US', subdivisionType: 'STATE', parentCode: 'US-FED', isActive: true, isSystem: true, createdAt: new Date('2024-01-01'), updatedAt: new Date('2024-01-01') },
  { id: '10000000-0000-0000-0000-0000000000b8', code: 'US-MO', name: 'Missouri', country: 'US', subdivisionType: 'STATE', parentCode: 'US-FED', isActive: true, isSystem: true, createdAt: new Date('2024-01-01'), updatedAt: new Date('2024-01-01') },
  { id: '10000000-0000-0000-0000-0000000000b9', code: 'US-MT', name: 'Montana', country: 'US', subdivisionType: 'STATE', parentCode: 'US-FED', isActive: true, isSystem: true, createdAt: new Date('2024-01-01'), updatedAt: new Date('2024-01-01') },
  { id: '10000000-0000-0000-0000-0000000000ba', code: 'US-NE', name: 'Nebraska', country: 'US', subdivisionType: 'STATE', parentCode: 'US-FED', isActive: true, isSystem: true, createdAt: new Date('2024-01-01'), updatedAt: new Date('2024-01-01') },
  { id: '10000000-0000-0000-0000-0000000000bb', code: 'US-NV', name: 'Nevada', country: 'US', subdivisionType: 'STATE', parentCode: 'US-FED', isActive: true, isSystem: true, createdAt: new Date('2024-01-01'), updatedAt: new Date('2024-01-01') },
  { id: '10000000-0000-0000-0000-0000000000bc', code: 'US-NH', name: 'New Hampshire', country: 'US', subdivisionType: 'STATE', parentCode: 'US-FED', isActive: true, isSystem: true, createdAt: new Date('2024-01-01'), updatedAt: new Date('2024-01-01') },
  { id: '10000000-0000-0000-0000-0000000000bd', code: 'US-NJ', name: 'New Jersey', country: 'US', subdivisionType: 'STATE', parentCode: 'US-FED', isActive: true, isSystem: true, createdAt: new Date('2024-01-01'), updatedAt: new Date('2024-01-01') },
  { id: '10000000-0000-0000-0000-0000000000be', code: 'US-NM', name: 'New Mexico', country: 'US', subdivisionType: 'STATE', parentCode: 'US-FED', isActive: true, isSystem: true, createdAt: new Date('2024-01-01'), updatedAt: new Date('2024-01-01') },
  { id: '10000000-0000-0000-0000-0000000000bf', code: 'US-NC', name: 'North Carolina', country: 'US', subdivisionType: 'STATE', parentCode: 'US-FED', isActive: true, isSystem: true, createdAt: new Date('2024-01-01'), updatedAt: new Date('2024-01-01') },
  { id: '10000000-0000-0000-0000-0000000000c1', code: 'US-ND', name: 'North Dakota', country: 'US', subdivisionType: 'STATE', parentCode: 'US-FED', isActive: true, isSystem: true, createdAt: new Date('2024-01-01'), updatedAt: new Date('2024-01-01') },
  { id: '10000000-0000-0000-0000-0000000000c2', code: 'US-OH', name: 'Ohio', country: 'US', subdivisionType: 'STATE', parentCode: 'US-FED', isActive: true, isSystem: true, createdAt: new Date('2024-01-01'), updatedAt: new Date('2024-01-01') },
  { id: '10000000-0000-0000-0000-0000000000c3', code: 'US-OR', name: 'Oregon', country: 'US', subdivisionType: 'STATE', parentCode: 'US-FED', isActive: true, isSystem: true, createdAt: new Date('2024-01-01'), updatedAt: new Date('2024-01-01') },
  { id: '10000000-0000-0000-0000-0000000000c4', code: 'US-PA', name: 'Pennsylvania', country: 'US', subdivisionType: 'STATE', parentCode: 'US-FED', isActive: true, isSystem: true, createdAt: new Date('2024-01-01'), updatedAt: new Date('2024-01-01') },
  { id: '10000000-0000-0000-0000-0000000000c5', code: 'US-RI', name: 'Rhode Island', country: 'US', subdivisionType: 'STATE', parentCode: 'US-FED', isActive: true, isSystem: true, createdAt: new Date('2024-01-01'), updatedAt: new Date('2024-01-01') },
  { id: '10000000-0000-0000-0000-0000000000c6', code: 'US-SC', name: 'South Carolina', country: 'US', subdivisionType: 'STATE', parentCode: 'US-FED', isActive: true, isSystem: true, createdAt: new Date('2024-01-01'), updatedAt: new Date('2024-01-01') },
  { id: '10000000-0000-0000-0000-0000000000c7', code: 'US-SD', name: 'South Dakota', country: 'US', subdivisionType: 'STATE', parentCode: 'US-FED', isActive: true, isSystem: true, createdAt: new Date('2024-01-01'), updatedAt: new Date('2024-01-01') },
  { id: '10000000-0000-0000-0000-0000000000c8', code: 'US-TN', name: 'Tennessee', country: 'US', subdivisionType: 'STATE', parentCode: 'US-FED', isActive: true, isSystem: true, createdAt: new Date('2024-01-01'), updatedAt: new Date('2024-01-01') },
  { id: '10000000-0000-0000-0000-0000000000c9', code: 'US-TX', name: 'Texas', country: 'US', subdivisionType: 'STATE', parentCode: 'US-FED', isActive: true, isSystem: true, createdAt: new Date('2024-01-01'), updatedAt: new Date('2024-01-01') },
  { id: '10000000-0000-0000-0000-0000000000ca', code: 'US-UT', name: 'Utah', country: 'US', subdivisionType: 'STATE', parentCode: 'US-FED', isActive: true, isSystem: true, createdAt: new Date('2024-01-01'), updatedAt: new Date('2024-01-01') },
  { id: '10000000-0000-0000-0000-0000000000cb', code: 'US-VT', name: 'Vermont', country: 'US', subdivisionType: 'STATE', parentCode: 'US-FED', isActive: true, isSystem: true, createdAt: new Date('2024-01-01'), updatedAt: new Date('2024-01-01') },
  { id: '10000000-0000-0000-0000-0000000000cc', code: 'US-VA', name: 'Virginia', country: 'US', subdivisionType: 'STATE', parentCode: 'US-FED', isActive: true, isSystem: true, createdAt: new Date('2024-01-01'), updatedAt: new Date('2024-01-01') },
  { id: '10000000-0000-0000-0000-0000000000cd', code: 'US-WV', name: 'West Virginia', country: 'US', subdivisionType: 'STATE', parentCode: 'US-FED', isActive: true, isSystem: true, createdAt: new Date('2024-01-01'), updatedAt: new Date('2024-01-01') },
  { id: '10000000-0000-0000-0000-0000000000ce', code: 'US-WI', name: 'Wisconsin', country: 'US', subdivisionType: 'STATE', parentCode: 'US-FED', isActive: true, isSystem: true, createdAt: new Date('2024-01-01'), updatedAt: new Date('2024-01-01') },
  { id: '10000000-0000-0000-0000-0000000000cf', code: 'US-WY', name: 'Wyoming', country: 'US', subdivisionType: 'STATE', parentCode: 'US-FED', isActive: true, isSystem: true, createdAt: new Date('2024-01-01'), updatedAt: new Date('2024-01-01') },
  {
    id: '10000000-0000-0000-0000-000000000009',
    code: 'CA',
    name: 'Canada',
    country: 'CA',
    subdivisionType: 'NATIONAL',
    parentCode: 'GLOBAL',
    isActive: true,
    isSystem: true,
    createdAt: new Date('2024-01-01'),
    updatedAt: new Date('2024-01-01'),
  },
  {
    id: '10000000-0000-0000-0000-000000000010',
    code: 'BR',
    name: 'Brazil',
    country: 'BR',
    subdivisionType: 'NATIONAL',
    parentCode: 'GLOBAL',
    isActive: true,
    isSystem: true,
    createdAt: new Date('2024-01-01'),
    updatedAt: new Date('2024-01-01'),
  },
  {
    id: '10000000-0000-0000-0000-000000000011',
    code: 'MX',
    name: 'Mexico',
    country: 'MX',
    subdivisionType: 'NATIONAL',
    parentCode: 'GLOBAL',
    isActive: true,
    isSystem: true,
    createdAt: new Date('2024-01-01'),
    updatedAt: new Date('2024-01-01'),
  },
  {
    id: '10000000-0000-0000-0000-000000000012',
    code: 'CO',
    name: 'Colombia',
    country: 'CO',
    subdivisionType: 'NATIONAL',
    parentCode: 'GLOBAL',
    isActive: true,
    isSystem: true,
    createdAt: new Date('2024-01-01'),
    updatedAt: new Date('2024-01-01'),
  },
  {
    id: '10000000-0000-0000-0000-000000000013',
    code: 'AR',
    name: 'Argentina',
    country: 'AR',
    subdivisionType: 'NATIONAL',
    parentCode: 'GLOBAL',
    isActive: true,
    isSystem: true,
    createdAt: new Date('2024-01-01'),
    updatedAt: new Date('2024-01-01'),
  },
  {
    id: '10000000-0000-0000-0000-000000000014',
    code: 'CL',
    name: 'Chile',
    country: 'CL',
    subdivisionType: 'NATIONAL',
    parentCode: 'GLOBAL',
    isActive: true,
    isSystem: true,
    createdAt: new Date('2024-01-01'),
    updatedAt: new Date('2024-01-01'),
  },
  {
    id: '10000000-0000-0000-0000-000000000015',
    code: 'PE',
    name: 'Peru',
    country: 'PE',
    subdivisionType: 'NATIONAL',
    parentCode: 'GLOBAL',
    isActive: true,
    isSystem: true,
    createdAt: new Date('2024-01-01'),
    updatedAt: new Date('2024-01-01'),
  },
];

// ============================================
// Rules (43 rules organized by jurisdiction)
// ============================================

const d = new Date('2024-01-01');

export const PRE_RULES: Rule[] = [
  // -----------------------------------------------
  // Global Baseline — Eligibility
  // -----------------------------------------------
  {
    id: '20000000-0000-0000-0000-000000000001',
    ruleId: 'PRE-ELIG-001',
    name: 'Global Eligibility Gate',
    description: 'Contact must be eligible (green flag) for the requested channel × purpose before any send is permitted.',
    jurisdictionCode: 'GLOBAL',
    layer: 'eligibility',
    channel: null,
    messagePurpose: null,
    b2bOrB2c: null,
    requiredChecks: [{ checkId: 'CHK_CONTACT_ELIGIBLE', required: true, params: null }],
    effect: 'BLOCK',
    remediation: 'Capture required opt-in evidence (or record a verbal consent event for informational where permitted) and update contact eligibility.',
    sourceRef: 'Telcom Laws: Part I §1.1 "Consent Standards" + Part II §3.1 "Express vs. Implied Consent"',
    priority: 0,
    isActive: true,
    isOverridable: false,
    isSystem: true,
    createdAt: d,
    updatedAt: d,
  },

  // -----------------------------------------------
  // Global Baseline — Constraints
  // -----------------------------------------------
  {
    id: '20000000-0000-0000-0000-000000000002',
    ruleId: 'PRE-GEN-001',
    name: 'Channel Normalization',
    description: 'RCS and SOCIAL_MESSAGE channels are normalized to SMS for policy evaluation. Attach RISK_CHANNEL_FALLBACK_TO_SMS.',
    jurisdictionCode: 'GLOBAL',
    layer: 'constraint',
    channel: null,
    messagePurpose: null,
    b2bOrB2c: null,
    requiredChecks: [],
    effect: 'CONDITION',
    remediation: null,
    sourceRef: 'Telcom Laws: Part I §1.1 (SMS/texting discussion) + Part II §3 (CASL CEMs include SMS)',
    priority: 1,
    isActive: true,
    isOverridable: false,
    isSystem: true,
    createdAt: d,
    updatedAt: d,
  },
  {
    id: '20000000-0000-0000-0000-000000000003',
    ruleId: 'PRE-GEN-002',
    name: 'Internal DNC Hard Stop',
    description: 'If recipient is on the internal DNC list, block the send on SMS/RCS and Voice channels (Email is not governed by the phone-based DNC list).',
    jurisdictionCode: 'GLOBAL',
    layer: 'constraint',
    channel: null,
    messagePurpose: null,
    b2bOrB2c: null,
    requiredChecks: [{ checkId: 'CHK_INTERNAL_DNC_CLEAR', required: true, params: null }],
    effect: 'BLOCK',
    remediation: '{ "code": "REMED_INTERNAL_DNC_CLEARANCE_REQUIRED", "reason": "INTERNAL_DNC_ON_LIST" }',
    sourceRef: 'Telcom Laws: Part I §1.2 "Internal DNC Lists"',
    priority: 2,
    isActive: true,
    isOverridable: false,
    isSystem: true,
    createdAt: d,
    updatedAt: d,
  },
  {
    id: '20000000-0000-0000-0000-000000000004',
    ruleId: 'PRE-GEN-003',
    name: 'National Registry Scrub Freshness',
    description: 'For marketing sends, verify national registry scrub is current (within cadence).',
    jurisdictionCode: 'GLOBAL',
    layer: 'constraint',
    channel: null,
    messagePurpose: 'MARKETING',
    b2bOrB2c: null,
    requiredChecks: [{ checkId: 'CHK_NATIONAL_REGISTRY_SCRUBBED', required: true, params: { scrubCadenceDays: 31 } }],
    effect: 'CONDITION',
    remediation: '{ "code": "COND_REGISTRY_SCRUB_REQUIRED", "maxAgeDays": 31 }',
    sourceRef: 'Telcom Laws: Part I §1.2 (US DNC); Part II §4.1 (Canada DNCL); Part III §6.1 (Mexico REPEP)',
    priority: 3,
    isActive: true,
    isOverridable: false,
    isSystem: true,
    createdAt: d,
    updatedAt: d,
  },
  {
    id: '20000000-0000-0000-0000-000000000005',
    ruleId: 'PRE-GEN-004',
    name: 'Record Retention Requirement',
    description: 'Maintain required records (consent, CDRs, scrub proofs) for the mandated retention period.',
    jurisdictionCode: 'GLOBAL',
    layer: 'constraint',
    channel: null,
    messagePurpose: null,
    b2bOrB2c: null,
    requiredChecks: [],
    effect: 'CONDITION',
    remediation: '{ "code": "COND_RECORD_RETENTION", "retentionYears": 2 }',
    sourceRef: 'Telcom Laws: Part IV §9 "Record Keeping and Data Hygiene"',
    priority: 4,
    isActive: true,
    isOverridable: false,
    isSystem: true,
    createdAt: d,
    updatedAt: d,
  },

  // -----------------------------------------------
  // United States (Federal) — Eligibility
  // -----------------------------------------------
  {
    id: '20000000-0000-0000-0000-000000000006',
    ruleId: 'PRE-US-FED-001',
    name: 'US Wireless Marketing Consent',
    description: 'TCPA requires appropriate consent for marketing SMS/Voice to wireless numbers.',
    jurisdictionCode: 'US-FED',
    layer: 'eligibility',
    channel: null,
    messagePurpose: 'MARKETING',
    b2bOrB2c: null,
    requiredChecks: [
      { checkId: 'CHK_WIRELESS_MARKETING_CONSENT_OK', required: true, params: { ifWirelessMinConsent: 'EXPRESS_OPTIN', ifLineTypeUnknown: 'riskFlag' } },
    ],
    effect: 'BLOCK',
    remediation: 'Obtain required consent for wireless marketing, or send via permitted mode.',
    sourceRef: 'Telcom Laws: Part I §1.1 "Consent Standards: The B2B/B2C Convergence"',
    priority: 10,
    isActive: true,
    isOverridable: false,
    isSystem: true,
    createdAt: d,
    updatedAt: d,
  },
  {
    id: '20000000-0000-0000-0000-000000000007',
    ruleId: 'PRE-US-FED-002',
    name: 'US Prerecorded Voice Written Consent',
    description: 'TCPA requires prior express written consent for marketing calls using prerecorded/artificial voice.',
    jurisdictionCode: 'US-FED',
    layer: 'eligibility',
    channel: 'VOICE',
    messagePurpose: 'MARKETING',
    b2bOrB2c: null,
    requiredChecks: [
      { checkId: 'CHK_CONSENT_AT_LEAST', required: true, params: { minConsent: 'WRITTEN_EXPRESS_OPTIN' } },
    ],
    effect: 'BLOCK',
    remediation: 'Obtain written express opt-in consent.',
    sourceRef: 'Telcom Laws: Part I §1.1 "Prerecorded Voices" + "Prior Express Written Consent"',
    priority: 11,
    isActive: true,
    isOverridable: false,
    isSystem: true,
    createdAt: d,
    updatedAt: d,
  },
  {
    id: '20000000-0000-0000-0000-000000000008',
    ruleId: 'PRE-US-FED-003',
    name: 'US Informational Express Consent (SMS)',
    description: 'TCPA requires prior express consent and immediate revocation handling for informational SMS. TCPA governs telephone channels only — informational email is governed by CAN-SPAM (PRE-US-FED-CANSPAM-001).',
    jurisdictionCode: 'US-FED',
    layer: 'eligibility',
    channel: 'SMS',
    messagePurpose: 'INFORMATIONAL',
    b2bOrB2c: null,
    requiredChecks: [
      { checkId: 'CHK_CONSENT_AT_LEAST', required: true, params: { minConsent: 'EXPRESS_OPTIN' } },
      { checkId: 'CHK_REVOCATION_HANDLING_ENABLED', required: true, params: null },
    ],
    effect: 'BLOCK',
    remediation: 'Capture prior express consent (or record verbal consent event) and enable immediate revocation handling.',
    sourceRef: 'Telcom Laws: Part I §1.1 "Prior Express Consent" + "Revocation of Consent"',
    priority: 12,
    isActive: true,
    isOverridable: false,
    isSystem: true,
    createdAt: d,
    updatedAt: d,
  },
  {
    id: '20000000-0000-0000-0000-000000000048',
    ruleId: 'PRE-US-FED-004',
    name: 'US Informational Express Consent (Voice)',
    description: 'TCPA requires prior express consent and immediate revocation handling for informational Voice calls. Voice variant of PRE-US-FED-003 (TCPA governs telephone channels only).',
    jurisdictionCode: 'US-FED',
    layer: 'eligibility',
    channel: 'VOICE',
    messagePurpose: 'INFORMATIONAL',
    b2bOrB2c: null,
    requiredChecks: [
      { checkId: 'CHK_CONSENT_AT_LEAST', required: true, params: { minConsent: 'EXPRESS_OPTIN' } },
      { checkId: 'CHK_REVOCATION_HANDLING_ENABLED', required: true, params: null },
    ],
    effect: 'BLOCK',
    remediation: 'Capture prior express consent (or record verbal consent event) and enable immediate revocation handling.',
    sourceRef: 'Telcom Laws: Part I §1.1 "Prior Express Consent" + "Revocation of Consent"',
    priority: 13,
    isActive: true,
    isOverridable: false,
    isSystem: true,
    createdAt: d,
    updatedAt: d,
  },

  // -----------------------------------------------
  // United States (Federal) — Constraints
  // -----------------------------------------------
  {
    id: '20000000-0000-0000-0000-000000000009',
    ruleId: 'PRE-US-DNC-001',
    name: 'US National DNC Scrub',
    description: 'Marketing voice calls to B2C must verify national DNC scrub is current.',
    jurisdictionCode: 'US-FED',
    layer: 'constraint',
    channel: 'VOICE',
    messagePurpose: 'MARKETING',
    b2bOrB2c: 'B2C',
    requiredChecks: [
      { checkId: 'CHK_NATIONAL_REGISTRY_SCRUBBED', required: true, params: { registry: 'US_DNC', maxAgeDays: 31 } },
    ],
    effect: 'CONDITION',
    remediation: '{ "code": "COND_REGISTRY_SCRUB_REQUIRED", "registry": "US_DNC", "maxAgeDays": 31 }',
    sourceRef: 'Telcom Laws: Part I §1.2 "National Do Not Call (DNC) Registry"',
    priority: 20,
    isActive: true,
    isOverridable: false,
    isSystem: true,
    createdAt: d,
    updatedAt: d,
  },
  {
    id: '20000000-0000-0000-0000-000000000010',
    ruleId: 'PRE-US-DNC-002',
    name: 'US National DNC Block',
    description: 'Marketing voice calls to B2C recipients on the national DNC are blocked.',
    jurisdictionCode: 'US-FED',
    layer: 'constraint',
    channel: 'VOICE',
    messagePurpose: 'MARKETING',
    b2bOrB2c: 'B2C',
    requiredChecks: [
      { checkId: 'CHK_NATIONAL_REGISTRY_CLEAR', required: true, params: { registry: 'US_DNC' } },
    ],
    effect: 'BLOCK',
    remediation: '{ "code": "REMED_REMOVE_FROM_CALL_LIST", "registry": "US_DNC" }',
    sourceRef: 'Telcom Laws: Part I §1.2 "National Do Not Call (DNC) Registry"',
    priority: 21,
    isActive: true,
    isOverridable: false,
    isSystem: true,
    createdAt: d,
    updatedAt: d,
  },
  {
    id: '20000000-0000-0000-0000-000000000011',
    ruleId: 'PRE-US-HRS-001',
    name: 'US Federal Calling Hours',
    description: 'Marketing voice calls restricted to 8:00 AM – 9:00 PM recipient-local time.',
    jurisdictionCode: 'US-FED',
    layer: 'constraint',
    channel: 'VOICE',
    messagePurpose: 'MARKETING',
    b2bOrB2c: null,
    requiredChecks: [
      { checkId: 'CHK_WITHIN_CALLING_HOURS', required: true, params: { window: 'US_FED_TELEMARKETING_08_21' } },
    ],
    effect: 'CONDITION',
    remediation: 'Defer the send to the next US federal telemarketing window (8:00 AM – 9:00 PM recipient-local).',
    sourceRef: 'Telcom Laws: Part I "Table 1: Comparative Analysis of Key US State Telemarketing Laws" (Federal row)',
    priority: 22,
    isActive: true,
    isOverridable: false,
    isSystem: true,
    createdAt: d,
    updatedAt: d,
  },
  {
    id: '20000000-0000-0000-0000-000000000044',
    ruleId: 'PRE-US-FED-CANSPAM-001',
    name: 'US CAN-SPAM Sender ID, Physical Address & Opt-Out',
    description: 'CAN-SPAM Act requires every commercial email to identify the sender, include a valid physical postal address, provide a working opt-out mechanism, honor opt-out within 10 business days, and use accurate "From"/Subject headers. The Act applies to all commercial email — including B2B.',
    jurisdictionCode: 'US-FED',
    layer: 'constraint',
    channel: 'EMAIL',
    messagePurpose: null,
    b2bOrB2c: null,
    requiredChecks: [
      {
        checkId: 'CHK_SENDER_ID_AND_UNSUB_PRESENT',
        required: true,
        params: {
          unsubscribeMaxDays: 10,
          unsubscribeBusinessDays: true,
          unsubscribeMechanismMinDays: 30,
          postalAddressRequired: true,
          headerAccuracyRequired: true,
          identifyAsAdRequired: true,
        },
      },
    ],
    effect: 'CONDITION',
    remediation: '{ "code": "COND_INCLUDE_SENDER_ID_AND_UNSUB", "unsubscribeMaxDays": 10, "unsubscribeBusinessDays": true, "postalAddressRequired": true, "headerAccuracyRequired": true }',
    sourceRef: '15 U.S.C. §7704 (CAN-SPAM Act of 2003); 16 CFR Part 316; FTC Compliance Guide.',
    priority: 25,
    isActive: true,
    isOverridable: false,
    isSystem: true,
    createdAt: d,
    updatedAt: d,
  },

  // -----------------------------------------------
  // Florida (FTSA)
  // -----------------------------------------------
  {
    id: '20000000-0000-0000-0000-000000000012',
    ruleId: 'PRE-US-FL-001',
    name: 'Florida Automation Restriction',
    description: 'FTSA restricts automated dialing/selection for marketing Voice/SMS to FL area codes.',
    jurisdictionCode: 'US-FL',
    layer: 'constraint',
    channel: null,
    messagePurpose: 'MARKETING',
    b2bOrB2c: null,
    requiredChecks: [
      { checkId: 'CHK_AUTOMATION_ALLOWED_FOR_SEND', required: true, params: { allowedIf: 'MANUAL_OR_ELEVATED_CONSENT' } },
    ],
    effect: 'CONDITION',
    remediation: 'Use manual/human-initiated workflows or obtain elevated consent.',
    sourceRef: 'Telcom Laws: Part I §2.1 "Florida: FTSA"',
    priority: 30,
    isActive: true,
    isOverridable: false,
    isSystem: true,
    createdAt: d,
    updatedAt: d,
  },
  {
    id: '20000000-0000-0000-0000-000000000013',
    ruleId: 'PRE-US-FL-002',
    name: 'Florida Frequency Cap (3/24h)',
    description: 'FTSA prohibits more than 3 commercial calls on the same subject within 24 hours to FL area codes.',
    jurisdictionCode: 'US-FL',
    layer: 'constraint',
    channel: null,
    messagePurpose: 'MARKETING',
    b2bOrB2c: null,
    requiredChecks: [
      { checkId: 'CHK_FREQUENCY_CAP_24H_OK', required: true, params: { cap: 3, subjectKey: 'required' } },
    ],
    effect: 'BLOCK',
    remediation: '{ "code": "REMED_RETRY_AFTER_WINDOW_RESETS", "windowHours": 24 }',
    sourceRef: 'Telcom Laws: Part I §2.1 "Call Frequency Cap"',
    priority: 31,
    isActive: true,
    isOverridable: false,
    isSystem: true,
    createdAt: d,
    updatedAt: d,
  },
  {
    id: '20000000-0000-0000-0000-000000000014',
    ruleId: 'PRE-US-FL-003',
    name: 'Florida Calling Hours (8am-8pm)',
    description: 'FTSA restricts marketing voice calls to 8:00 AM – 8:00 PM local time for FL area codes.',
    jurisdictionCode: 'US-FL',
    layer: 'constraint',
    channel: 'VOICE',
    messagePurpose: 'MARKETING',
    b2bOrB2c: null,
    requiredChecks: [
      { checkId: 'CHK_WITHIN_CALLING_HOURS', required: true, params: { window: 'US_FL_08_20' } },
    ],
    effect: 'CONDITION',
    remediation: 'Defer the send to the next Florida calling window (8:00 AM – 8:00 PM recipient-local).',
    sourceRef: 'Telcom Laws: Part I §2.1 "Calling Hours"',
    priority: 32,
    isActive: true,
    isOverridable: false,
    isSystem: true,
    createdAt: d,
    updatedAt: d,
  },
  {
    id: '20000000-0000-0000-0000-000000000057',
    ruleId: 'PRE-US-FL-004',
    name: 'Florida Written Consent Requirement',
    description: 'FTSA (Fla. Stat. §501.059) requires written express consent for automated marketing Voice/SMS to FL recipients.',
    jurisdictionCode: 'US-FL',
    layer: 'eligibility',
    channel: null,
    messagePurpose: 'MARKETING',
    b2bOrB2c: null,
    requiredChecks: [
      { checkId: 'CHK_CONSENT_AT_LEAST', required: true, params: { minConsent: 'WRITTEN_EXPRESS_OPTIN' } },
    ],
    effect: 'BLOCK',
    remediation: 'Obtain written express opt-in consent.',
    sourceRef: 'Telcom Laws: Part I §2.1 "Florida: FTSA"',
    priority: 33,
    isActive: true,
    isOverridable: false,
    isSystem: true,
    createdAt: d,
    updatedAt: d,
  },

  // -----------------------------------------------
  // Oklahoma
  // -----------------------------------------------
  {
    id: '20000000-0000-0000-0000-000000000015',
    ruleId: 'PRE-US-OK-001',
    name: 'Oklahoma Frequency Cap (3/24h)',
    description: 'Oklahoma Mini-TCPA prohibits more than 3 commercial calls on the same subject within 24 hours.',
    jurisdictionCode: 'US-OK',
    layer: 'constraint',
    channel: null,
    messagePurpose: 'MARKETING',
    b2bOrB2c: null,
    requiredChecks: [
      { checkId: 'CHK_FREQUENCY_CAP_24H_OK', required: true, params: { cap: 3, subjectKey: 'required' } },
    ],
    effect: 'BLOCK',
    remediation: '{ "code": "REMED_RETRY_AFTER_WINDOW_RESETS", "windowHours": 24 }',
    sourceRef: 'Telcom Laws: Part I §2.2 "Oklahoma"',
    priority: 40,
    isActive: true,
    isOverridable: false,
    isSystem: true,
    createdAt: d,
    updatedAt: d,
  },
  {
    id: '20000000-0000-0000-0000-000000000016',
    ruleId: 'PRE-US-OK-002',
    name: 'Oklahoma Calling Hours (8am-8pm)',
    description: 'Oklahoma restricts marketing voice calls to 8:00 AM – 8:00 PM local time.',
    jurisdictionCode: 'US-OK',
    layer: 'constraint',
    channel: 'VOICE',
    messagePurpose: 'MARKETING',
    b2bOrB2c: null,
    requiredChecks: [
      { checkId: 'CHK_WITHIN_CALLING_HOURS', required: true, params: { window: 'US_OK_08_20' } },
    ],
    effect: 'CONDITION',
    remediation: 'Defer the send to the next Oklahoma calling window (8:00 AM – 8:00 PM recipient-local).',
    sourceRef: 'Telcom Laws: Part I §2.2 "Calling Hours"',
    priority: 41,
    isActive: true,
    isOverridable: false,
    isSystem: true,
    createdAt: d,
    updatedAt: d,
  },
  {
    id: '20000000-0000-0000-0000-000000000056',
    ruleId: 'PRE-US-OK-HRS-SMS-001',
    name: 'Oklahoma Calling Hours (SMS)',
    description: 'Oklahoma Telemarketing Act (15 O.S. §775A) restricts marketing text messages to 8:00 AM – 8:00 PM local time.',
    jurisdictionCode: 'US-OK',
    layer: 'constraint',
    channel: 'SMS',
    messagePurpose: 'MARKETING',
    b2bOrB2c: null,
    requiredChecks: [
      { checkId: 'CHK_WITHIN_CALLING_HOURS', required: true, params: { window: 'US_OK_SMS_08_20' } },
    ],
    effect: 'CONDITION',
    remediation: 'Defer the send to the next Oklahoma calling window (8:00 AM – 8:00 PM recipient-local).',
    sourceRef: 'Oklahoma Telephone Solicitation Act of 2022 (covers texts)',
    priority: 43,
    isActive: true,
    isOverridable: false,
    isSystem: true,
    createdAt: d,
    updatedAt: d,
  },

  // -----------------------------------------------
  // Washington (CSTA)
  // -----------------------------------------------
  {
    id: '20000000-0000-0000-0000-000000000017',
    ruleId: 'PRE-US-WA-001',
    name: 'Washington 10-Second Termination Rule',
    description: 'Washington CSTA requires the voice system to end the call within 10 seconds if the called party indicates they want to end the call.',
    jurisdictionCode: 'US-WA',
    layer: 'constraint',
    channel: 'VOICE',
    messagePurpose: 'MARKETING',
    b2bOrB2c: null,
    requiredChecks: [
      { checkId: 'CHK_END_CALL_WITHIN_10S_ON_REQUEST', required: true, params: { maxSeconds: 10 } },
    ],
    effect: 'CONDITION',
    remediation: 'Ensure call flow enforces 10-second termination on request.',
    sourceRef: 'Telcom Laws: Part I §2.3 "The 10-Second Rule"',
    priority: 50,
    isActive: true,
    isOverridable: false,
    isSystem: true,
    createdAt: d,
    updatedAt: d,
  },
  {
    id: '20000000-0000-0000-0000-000000000018',
    ruleId: 'PRE-US-WA-002',
    name: 'Washington Calling Hours (8am-8pm)',
    description: 'Washington CSTA restricts marketing voice calls to 8:00 AM – 8:00 PM local time.',
    jurisdictionCode: 'US-WA',
    layer: 'constraint',
    channel: 'VOICE',
    messagePurpose: 'MARKETING',
    b2bOrB2c: null,
    requiredChecks: [
      { checkId: 'CHK_WITHIN_CALLING_HOURS', required: true, params: { window: 'US_WA_08_20' } },
    ],
    effect: 'CONDITION',
    remediation: 'Defer the send to the next Washington calling window (8:00 AM – 8:00 PM recipient-local).',
    sourceRef: 'Telcom Laws: Part I §2.3 "Calling Hours"',
    priority: 51,
    isActive: true,
    isOverridable: false,
    isSystem: true,
    createdAt: d,
    updatedAt: d,
  },
  {
    id: '20000000-0000-0000-0000-000000000058',
    ruleId: 'PRE-US-WA-003',
    name: 'Washington Written Consent Requirement',
    description: 'Washington CSTA (RCW 19.158) requires written express consent for automated marketing Voice/SMS to WA recipients.',
    jurisdictionCode: 'US-WA',
    layer: 'eligibility',
    channel: null,
    messagePurpose: 'MARKETING',
    b2bOrB2c: null,
    requiredChecks: [
      { checkId: 'CHK_CONSENT_AT_LEAST', required: true, params: { minConsent: 'WRITTEN_EXPRESS_OPTIN' } },
    ],
    effect: 'BLOCK',
    remediation: 'Obtain written express opt-in consent.',
    sourceRef: 'Telcom Laws: Part I §2.3 "Washington: CSTA"',
    priority: 52,
    isActive: true,
    isOverridable: false,
    isSystem: true,
    createdAt: d,
    updatedAt: d,
  },

  // -----------------------------------------------
  // Maryland
  // -----------------------------------------------
  {
    id: '20000000-0000-0000-0000-000000000019',
    ruleId: 'PRE-US-MD-001',
    name: 'Maryland Written Consent Requirement',
    description: 'Maryland Stop the Spam Calls Act requires written express consent for automated marketing Voice/SMS.',
    jurisdictionCode: 'US-MD',
    layer: 'eligibility',
    channel: null,
    messagePurpose: 'MARKETING',
    b2bOrB2c: null,
    requiredChecks: [
      { checkId: 'CHK_CONSENT_AT_LEAST', required: true, params: { minConsent: 'WRITTEN_EXPRESS_OPTIN' } },
    ],
    effect: 'BLOCK',
    remediation: 'Obtain written express opt-in consent.',
    sourceRef: 'Telcom Laws: Part I §2.4 "Autodialer Broadening"',
    priority: 60,
    isActive: true,
    isOverridable: false,
    isSystem: true,
    createdAt: d,
    updatedAt: d,
  },
  {
    id: '20000000-0000-0000-0000-000000000020',
    ruleId: 'PRE-US-MD-002',
    name: 'Maryland Frequency Cap',
    description: 'Maryland enforces a 3/24h frequency cap for marketing voice.',
    jurisdictionCode: 'US-MD',
    layer: 'constraint',
    channel: 'VOICE',
    messagePurpose: 'MARKETING',
    b2bOrB2c: null,
    requiredChecks: [
      { checkId: 'CHK_FREQUENCY_CAP_24H_OK', required: true, params: { cap: 3 } },
    ],
    effect: 'BLOCK',
    remediation: '{ "code": "REMED_RETRY_AFTER_WINDOW_RESETS", "windowHours": 24 }',
    sourceRef: 'Telcom Laws: Part I §2.4 "Frequency and Hours"',
    priority: 61,
    isActive: true,
    isOverridable: false,
    isSystem: true,
    createdAt: d,
    updatedAt: d,
  },
  {
    // Split out of the former "Frequency Cap & Hours" rule so an out-of-hours
    // send DEFERS (CONDITION → scheduler picks the next window) while a
    // frequency-cap violation still hard-BLOCKS. A single rule could not do
    // both because it carries one effect for all of its checks.
    id: '20000000-0000-0000-0000-000000000049',
    ruleId: 'PRE-US-MD-003',
    name: 'Maryland Calling Hours',
    description: 'Maryland restricts marketing voice calls to 8:00 AM – 8:00 PM recipient-local time.',
    jurisdictionCode: 'US-MD',
    layer: 'constraint',
    channel: 'VOICE',
    messagePurpose: 'MARKETING',
    b2bOrB2c: null,
    requiredChecks: [
      { checkId: 'CHK_WITHIN_CALLING_HOURS', required: true, params: { window: 'US_MD_08_20' } },
    ],
    effect: 'CONDITION',
    remediation: 'Defer the send to the next Maryland calling window (8:00 AM – 8:00 PM recipient-local).',
    sourceRef: 'Telcom Laws: Part I §2.4 "Frequency and Hours"',
    priority: 62,
    isActive: true,
    isOverridable: false,
    isSystem: true,
    createdAt: d,
    updatedAt: d,
  },

  // -----------------------------------------------
  // New York
  // -----------------------------------------------
  {
    id: '20000000-0000-0000-0000-000000000021',
    ruleId: 'PRE-US-NY-001',
    name: 'New York DNC Disclosure at Call Start',
    description: 'New York requires DNC disclosure immediately after identification at the beginning of marketing voice calls.',
    jurisdictionCode: 'US-NY',
    layer: 'constraint',
    channel: 'VOICE',
    messagePurpose: 'MARKETING',
    b2bOrB2c: null,
    requiredChecks: [
      { checkId: 'CHK_DNC_DISCLOSURE_AT_CALL_START', required: true, params: { placement: 'immediately_after_identification' } },
    ],
    effect: 'CONDITION',
    remediation: 'Add disclosure to call script and enforce placement.',
    sourceRef: 'Telcom Laws: Part I §2.5 "Immediate DNC Disclosure"',
    priority: 70,
    isActive: true,
    isOverridable: false,
    isSystem: true,
    createdAt: d,
    updatedAt: d,
  },

  // -----------------------------------------------
  // Arizona
  // -----------------------------------------------
  {
    id: '20000000-0000-0000-0000-000000000022',
    ruleId: 'PRE-US-AZ-001',
    name: 'Arizona SMS DNC Block',
    description: 'Arizona H.B. 2498 prohibits marketing text messages to numbers on the National DNC registry.',
    jurisdictionCode: 'US-AZ',
    layer: 'constraint',
    channel: 'SMS',
    messagePurpose: 'MARKETING',
    b2bOrB2c: null,
    requiredChecks: [
      { checkId: 'CHK_NATIONAL_REGISTRY_CLEAR', required: true, params: { registry: 'US_DNC', appliesTo: 'SMS_MARKETING' } },
    ],
    effect: 'BLOCK',
    remediation: 'Remove from campaign; send only if not on DNC (or supported exemption).',
    sourceRef: 'Telcom Laws: Part I §2.6 "Arizona"',
    priority: 80,
    isActive: true,
    isOverridable: false,
    isSystem: true,
    createdAt: d,
    updatedAt: d,
  },

  // -----------------------------------------------
  // Canada — Eligibility (CASL)
  // -----------------------------------------------
  {
    id: '20000000-0000-0000-0000-000000000023',
    ruleId: 'PRE-CA-CASL-001',
    name: 'CASL Consent Requirement',
    description: 'CASL requires express or valid implied consent for marketing EMAIL/SMS to Canadian recipients.',
    jurisdictionCode: 'CA',
    layer: 'eligibility',
    channel: null,
    messagePurpose: 'MARKETING',
    b2bOrB2c: null,
    requiredChecks: [
      { checkId: 'CHK_CASL_CONSENT_VALID', required: true, params: { impliedPurchaseWindowMonths: 24, impliedInquiryWindowMonths: 6 } },
    ],
    effect: 'BLOCK',
    remediation: 'Obtain express opt-in or validate implied-consent window.',
    sourceRef: 'Telcom Laws: Part II §3.1 "Express vs. Implied Consent"',
    priority: 100,
    isActive: true,
    isOverridable: false,
    isSystem: true,
    createdAt: d,
    updatedAt: d,
  },
  {
    id: '20000000-0000-0000-0000-000000000047',
    ruleId: 'PRE-CA-CASL-INFO-001',
    name: 'CASL Informational Consent Requirement',
    description: 'CASL §6: commercial electronic messages (email/SMS) to Canadian recipients require express or valid implied consent regardless of marketing vs informational classification. Closes the informational-trap from issue #757 §6a. Implied consent must be backed by an active EBR (PURCHASE within 24mo, INQUIRY within 6mo) — see ConsentRecord.expiresAt.',
    jurisdictionCode: 'CA',
    layer: 'eligibility',
    channel: null,
    messagePurpose: 'INFORMATIONAL',
    b2bOrB2c: null,
    requiredChecks: [
      { checkId: 'CHK_CASL_CONSENT_VALID', required: true, params: { impliedPurchaseWindowMonths: 24, impliedInquiryWindowMonths: 6 } },
    ],
    effect: 'BLOCK',
    remediation: 'Obtain express opt-in or capture an EBR event (purchase/inquiry) with timestamp.',
    sourceRef: 'CASL §6 + Industry Canada implied-consent guidance',
    priority: 105,
    isActive: true,
    isOverridable: false,
    isSystem: true,
    createdAt: d,
    updatedAt: d,
  },
  {
    id: '20000000-0000-0000-0000-000000000024',
    ruleId: 'PRE-CA-CASL-003',
    name: 'CASL Conspicuous Publication (B2B)',
    description: 'Canada B2B conspicuous publication exemption requires evidence snapshot and relevance rationale.',
    jurisdictionCode: 'CA',
    layer: 'eligibility',
    channel: 'EMAIL',
    messagePurpose: 'MARKETING',
    b2bOrB2c: 'B2B',
    requiredChecks: [
      { checkId: 'CHK_CASL_CONSPICUOUS_PUBLICATION_VALID', required: true, params: { requiresEvidenceSnapshot: true, requiresRelevance: true, requiresNoDisclaimer: true } },
    ],
    effect: 'CONDITION',
    remediation: 'Store evidence snapshot and relevance rationale; otherwise require express opt-in.',
    sourceRef: 'Telcom Laws: Part II §3.2 "Conspicuous Publication"',
    priority: 101,
    isActive: true,
    isOverridable: false,
    isSystem: true,
    createdAt: d,
    updatedAt: d,
  },

  // -----------------------------------------------
  // Canada — Constraints
  // -----------------------------------------------
  {
    id: '20000000-0000-0000-0000-000000000025',
    ruleId: 'PRE-CA-CASL-002',
    name: 'CASL Content & Form Requirements',
    description: 'CASL requires sender identification, unsubscribe mechanism, and revocation handling for all CEM messages.',
    jurisdictionCode: 'CA',
    layer: 'constraint',
    channel: null,
    messagePurpose: null,
    b2bOrB2c: null,
    requiredChecks: [
      { checkId: 'CHK_SENDER_ID_AND_UNSUB_PRESENT', required: true, params: { unsubscribeMaxDays: 10 } },
      { checkId: 'CHK_REVOCATION_HANDLING_ENABLED', required: true, params: null },
    ],
    userFacingSummary:
      'Message includes sender identification and a working unsubscribe option (honored within 10 days), and the system processes opt-out/STOP requests immediately.',
    effect: 'CONDITION',
    remediation: 'Add required identification/unsubscribe and process unsubscribes within required window.',
    sourceRef: 'Telcom Laws: Part II §3.3 "Content and Form Requirements"',
    priority: 102,
    isActive: true,
    isOverridable: false,
    isSystem: true,
    createdAt: d,
    updatedAt: d,
  },
  {
    id: '20000000-0000-0000-0000-000000000026',
    ruleId: 'PRE-CA-DNCL-001',
    name: 'Canada DNCL Scrub (B2C Voice)',
    description: 'Marketing voice calls to B2C Canadian recipients must verify DNCL scrub is current.',
    jurisdictionCode: 'CA',
    layer: 'constraint',
    channel: 'VOICE',
    messagePurpose: 'MARKETING',
    b2bOrB2c: 'B2C',
    requiredChecks: [
      { checkId: 'CHK_NATIONAL_REGISTRY_SCRUBBED', required: true, params: { registry: 'CA_DNCL', maxAgeDays: 31 } },
    ],
    effect: 'CONDITION',
    remediation: '{ "code": "COND_REGISTRY_SCRUB_REQUIRED", "registry": "CA_DNCL", "maxAgeDays": 31 }',
    sourceRef: 'Telcom Laws: Part II §4.1 "National DNCL and the B2B Exemption"',
    priority: 103,
    isActive: true,
    isOverridable: false,
    isSystem: true,
    createdAt: d,
    updatedAt: d,
  },
  {
    id: '20000000-0000-0000-0000-000000000027',
    ruleId: 'PRE-CA-DNCL-002',
    name: 'Canada B2B Internal DNC',
    description: 'B2B voice calls in Canada are exempt from DNCL but must honor internal DNC.',
    jurisdictionCode: 'CA',
    layer: 'constraint',
    channel: 'VOICE',
    messagePurpose: 'MARKETING',
    b2bOrB2c: 'B2B',
    requiredChecks: [
      { checkId: 'CHK_INTERNAL_DNC_CLEAR', required: true, params: { dnclApplies: false } },
    ],
    effect: 'CONDITION',
    remediation: 'Honor internal DNC and maintain call records.',
    sourceRef: 'Telcom Laws: Part II §4.1 "B2B Exemption"',
    priority: 104,
    isActive: true,
    isOverridable: false,
    isSystem: true,
    createdAt: d,
    updatedAt: d,
  },
  {
    id: '20000000-0000-0000-0000-000000000028',
    ruleId: 'PRE-CA-ADAD-001',
    name: 'Canada ADAD (Robocall) Consent',
    description: 'Canada ADAD rules require express opt-in consent for prerecorded marketing voice calls.',
    jurisdictionCode: 'CA',
    layer: 'constraint',
    channel: 'VOICE',
    messagePurpose: 'MARKETING',
    b2bOrB2c: null,
    requiredChecks: [
      { checkId: 'CHK_CONSENT_AT_LEAST', required: true, params: { minConsent: 'EXPRESS_OPTIN' } },
    ],
    effect: 'BLOCK',
    remediation: 'Obtain express opt-in consent.',
    sourceRef: 'Telcom Laws: Part II §4.2 "ADAD Rules (Robocalls)"',
    priority: 105,
    isActive: true,
    isOverridable: false,
    isSystem: true,
    createdAt: d,
    updatedAt: d,
  },
  {
    id: '20000000-0000-0000-0000-000000000029',
    ruleId: 'PRE-CA-HRS-001',
    name: 'Canada Calling Hours',
    description: 'Canada UTR restricts marketing voice calls to specific hours by day of week.',
    jurisdictionCode: 'CA',
    layer: 'constraint',
    channel: 'VOICE',
    messagePurpose: 'MARKETING',
    b2bOrB2c: null,
    requiredChecks: [
      { checkId: 'CHK_WITHIN_CALLING_HOURS', required: true, params: { window: 'CA_UTR_TELEMARKETING' } },
    ],
    effect: 'CONDITION',
    remediation: 'Defer the send to the next Canadian UTR telemarketing window.',
    sourceRef: 'Telcom Laws: Part II §4.3 "Calling Hours"',
    priority: 106,
    isActive: true,
    isOverridable: false,
    isSystem: true,
    createdAt: d,
    updatedAt: d,
  },

  // -----------------------------------------------
  // Brazil
  // -----------------------------------------------
  {
    id: '20000000-0000-0000-0000-000000000030',
    ruleId: 'PRE-BR-LGPD-001',
    name: 'Brazil LGPD Lawful Basis',
    description: 'LGPD requires a lawful basis (consent or legitimate interest with LIA) for marketing communications.',
    jurisdictionCode: 'BR',
    layer: 'eligibility',
    channel: null,
    messagePurpose: 'MARKETING',
    b2bOrB2c: null,
    requiredChecks: [
      { checkId: 'CHK_LGPD_LAWFUL_BASIS_VALID', required: true, params: { preferred: 'CONSENT', allowLegitimateInterestWithLIA: true } },
    ],
    effect: 'BLOCK',
    remediation: 'Obtain explicit opt-in or document legitimate interest assessment (LIA).',
    sourceRef: 'Telcom Laws: Part III §5.1 "Legal Bases for Marketing"',
    priority: 110,
    isActive: true,
    isOverridable: false,
    isSystem: true,
    createdAt: d,
    updatedAt: d,
  },
  {
    id: '20000000-0000-0000-0000-000000000031',
    ruleId: 'PRE-BR-TEL-001',
    name: 'Brazil 0303 Prefix & Não Me Perturbe',
    description: 'Brazil requires 0303 prefix for marketing voice calls and suppression via Não Me Perturbe registry.',
    jurisdictionCode: 'BR',
    layer: 'constraint',
    channel: 'VOICE',
    messagePurpose: 'MARKETING',
    b2bOrB2c: null,
    requiredChecks: [
      { checkId: 'CHK_BRAZIL_0303_PREFIX', required: true, params: { appliesToSectors: 'telecom_finance' } },
      { checkId: 'CHK_BRAZIL_NAO_ME_PERTURBE_CLEAR', required: true, params: null },
    ],
    effect: 'CONDITION',
    remediation: 'Use compliant 0303 prefix and suppress where Não Me Perturbe applies.',
    sourceRef: 'Telcom Laws: Part III §5.2 "0303 Prefix Mandate" + "Não Me Perturbe"',
    priority: 111,
    isActive: true,
    isOverridable: false,
    isSystem: true,
    createdAt: d,
    updatedAt: d,
  },
  {
    id: '20000000-0000-0000-0000-000000000032',
    ruleId: 'PRE-BR-HRS-001',
    name: 'Brazil Calling Hours',
    description: 'Brazil restricts marketing voice calls by state law (generally 8am-8pm weekdays, 8am-2pm Sat, no Sunday).',
    jurisdictionCode: 'BR',
    layer: 'constraint',
    channel: 'VOICE',
    messagePurpose: 'MARKETING',
    b2bOrB2c: null,
    requiredChecks: [
      { checkId: 'CHK_WITHIN_CALLING_HOURS', required: true, params: { window: 'BR_TELEMARKETING' } },
    ],
    effect: 'CONDITION',
    remediation: 'Restrict calls to allowed windows.',
    sourceRef: 'Telcom Laws: Part III §5.2 "Calling Hours"',
    priority: 112,
    isActive: true,
    isOverridable: false,
    isSystem: true,
    createdAt: d,
    updatedAt: d,
  },

  // -----------------------------------------------
  // Mexico
  // -----------------------------------------------
  {
    id: '20000000-0000-0000-0000-000000000033',
    ruleId: 'PRE-MX-REPEP-001',
    name: 'Mexico REPEP Scrub',
    description: 'Marketing voice calls must verify REPEP registry scrub is current (within 30 days).',
    jurisdictionCode: 'MX',
    layer: 'constraint',
    channel: 'VOICE',
    messagePurpose: 'MARKETING',
    b2bOrB2c: null,
    requiredChecks: [
      { checkId: 'CHK_NATIONAL_REGISTRY_SCRUBBED', required: true, params: { registry: 'MX_REPEP', maxAgeDays: 30 } },
    ],
    effect: 'CONDITION',
    remediation: '{ "code": "COND_REGISTRY_SCRUB_REQUIRED", "registry": "MX_REPEP", "maxAgeDays": 30 }',
    sourceRef: 'Telcom Laws: Part III §6.1 "REPEP Registry"',
    priority: 120,
    isActive: true,
    isOverridable: false,
    isSystem: true,
    createdAt: d,
    updatedAt: d,
  },
  {
    id: '20000000-0000-0000-0000-000000000034',
    ruleId: 'PRE-MX-REPEP-002',
    name: 'Mexico REPEP Block',
    description: 'Marketing voice calls to recipients on the REPEP registry are blocked.',
    jurisdictionCode: 'MX',
    layer: 'constraint',
    channel: 'VOICE',
    messagePurpose: 'MARKETING',
    b2bOrB2c: null,
    requiredChecks: [
      { checkId: 'CHK_NATIONAL_REGISTRY_CLEAR', required: true, params: { registry: 'MX_REPEP' } },
    ],
    effect: 'BLOCK',
    remediation: '{ "code": "REMED_REMOVE_FROM_CALL_LIST", "registry": "MX_REPEP" }',
    sourceRef: 'Telcom Laws: Part III §6.1 "REPEP Registry"',
    priority: 121,
    isActive: true,
    isOverridable: false,
    isSystem: true,
    createdAt: d,
    updatedAt: d,
  },
  {
    id: '20000000-0000-0000-0000-000000000045',
    ruleId: 'PRE-MX-REUS-001',
    name: 'Mexico REUS Scrub (Financial Sector)',
    description: 'CONDUSEF requires financial-sector entities (banks, insurance, investment funds, trust managers) to consult the REUS registry before any promotional outreach to users of financial services. Applied in addition to PROFECO REPEP scrubbing. Rule fires only when the tenant is in the financial sector.',
    jurisdictionCode: 'MX',
    layer: 'constraint',
    channel: null,
    messagePurpose: 'MARKETING',
    b2bOrB2c: null,
    tenantIndustry: 'FINANCIAL_SERVICES',
    requiredChecks: [
      { checkId: 'CHK_NATIONAL_REGISTRY_SCRUBBED', required: true, params: { registry: 'MX_REUS', maxAgeDays: 30 } },
    ],
    effect: 'CONDITION',
    remediation: '{ "code": "COND_REGISTRY_SCRUB_REQUIRED", "registry": "MX_REUS", "maxAgeDays": 30 }',
    sourceRef: 'CONDUSEF — Registro Público de Usuarios (REUS). Ley para la Transparencia y Ordenamiento de los Servicios Financieros, Art. 8.',
    priority: 123,
    isActive: true,
    isOverridable: false,
    isSystem: true,
    createdAt: d,
    updatedAt: d,
  },
  {
    id: '20000000-0000-0000-0000-000000000046',
    ruleId: 'PRE-MX-REUS-002',
    name: 'Mexico REUS Block (Financial Sector)',
    description: 'Promotional outreach by financial-sector entities to recipients listed on REUS is prohibited. Rule fires only when the tenant is in the financial sector.',
    jurisdictionCode: 'MX',
    layer: 'constraint',
    channel: null,
    messagePurpose: 'MARKETING',
    b2bOrB2c: null,
    tenantIndustry: 'FINANCIAL_SERVICES',
    requiredChecks: [
      { checkId: 'CHK_NATIONAL_REGISTRY_CLEAR', required: true, params: { registry: 'MX_REUS' } },
    ],
    effect: 'BLOCK',
    remediation: '{ "code": "REMED_REMOVE_FROM_CALL_LIST", "registry": "MX_REUS" }',
    sourceRef: 'CONDUSEF — Registro Público de Usuarios (REUS). Ley para la Transparencia y Ordenamiento de los Servicios Financieros, Art. 8.',
    priority: 124,
    isActive: true,
    isOverridable: false,
    isSystem: true,
    createdAt: d,
    updatedAt: d,
  },
  {
    id: '20000000-0000-0000-0000-000000000035',
    ruleId: 'PRE-MX-HRS-001',
    name: 'Mexico Calling Hours',
    description: 'Mexico restricts marketing voice calls generally to Mon-Fri 8:00 AM – 7:00 PM.',
    jurisdictionCode: 'MX',
    layer: 'constraint',
    channel: 'VOICE',
    messagePurpose: 'MARKETING',
    b2bOrB2c: null,
    requiredChecks: [
      { checkId: 'CHK_WITHIN_CALLING_HOURS', required: true, params: { window: 'MX_TELEMARKETING' } },
    ],
    effect: 'CONDITION',
    remediation: 'Restrict calls to allowed windows.',
    sourceRef: 'Telcom Laws: Part III §6.3 "Calling Hours"',
    priority: 122,
    isActive: true,
    isOverridable: false,
    isSystem: true,
    createdAt: d,
    updatedAt: d,
  },

  // -----------------------------------------------
  // Colombia
  // -----------------------------------------------
  {
    id: '20000000-0000-0000-0000-000000000036',
    ruleId: 'PRE-CO-FREQ-001',
    name: 'Colombia Cross-Channel Frequency Cap (1/7d)',
    description: 'Law 2300 limits marketing contact to once per week per subject, across all channels.',
    jurisdictionCode: 'CO',
    layer: 'constraint',
    channel: null,
    messagePurpose: 'MARKETING',
    b2bOrB2c: null,
    requiredChecks: [
      { checkId: 'CHK_FREQUENCY_CAP_7D_OK', required: true, params: { cap: 1, subjectKey: 'required', crossChannel: true } },
    ],
    effect: 'BLOCK',
    remediation: '{ "code": "REMED_RETRY_AFTER_WINDOW_RESETS", "windowDays": 7, "cap": 1, "crossChannel": true }',
    sourceRef: 'Telcom Laws: Part III §7.1 "Frequency Cap"',
    priority: 130,
    isActive: true,
    isOverridable: false,
    isSystem: true,
    createdAt: d,
    updatedAt: d,
  },
  {
    id: '20000000-0000-0000-0000-000000000037',
    ruleId: 'PRE-CO-CHAN-001',
    name: 'Colombia Channel Preference',
    description: 'Law 2300 enforces recipient channel preference. If preferred channel is set, only that channel is permitted.',
    jurisdictionCode: 'CO',
    layer: 'constraint',
    channel: null,
    messagePurpose: null,
    b2bOrB2c: null,
    requiredChecks: [
      { checkId: 'CHK_CHANNEL_PREFERENCE_MATCH', required: true, params: { ifPreferredChannelSet: 'enforce' } },
    ],
    effect: 'BLOCK',
    remediation: 'Send via preferred channel only.',
    sourceRef: 'Telcom Laws: Part III §7.2 "Channel Preference"',
    priority: 131,
    isActive: true,
    isOverridable: false,
    isSystem: true,
    createdAt: d,
    updatedAt: d,
  },
  {
    id: '20000000-0000-0000-0000-000000000038',
    ruleId: 'PRE-CO-HRS-001',
    name: 'Colombia Strict Contact Hours',
    description: 'Colombia restricts marketing voice calls to Mon-Fri 7am-7pm, Sat 8am-3pm, no Sunday/holidays.',
    jurisdictionCode: 'CO',
    layer: 'constraint',
    channel: 'VOICE',
    messagePurpose: 'MARKETING',
    b2bOrB2c: null,
    requiredChecks: [
      { checkId: 'CHK_WITHIN_CALLING_HOURS', required: true, params: { window: 'CO_STRICT_CONTACT_HOURS' } },
    ],
    effect: 'CONDITION',
    remediation: 'Defer the send to the next Colombian permitted contact window.',
    sourceRef: 'Telcom Laws: Part III §7.2 "Strict Hours"',
    priority: 132,
    isActive: true,
    isOverridable: false,
    isSystem: true,
    createdAt: d,
    updatedAt: d,
  },

  // -----------------------------------------------
  // Argentina
  // -----------------------------------------------
  {
    id: '20000000-0000-0000-0000-000000000039',
    ruleId: 'PRE-AR-NOLLAME-001',
    name: 'Argentina No Llame Registry Block',
    description: 'Argentina No Llame registry blocks marketing voice calls to registered numbers.',
    jurisdictionCode: 'AR',
    layer: 'constraint',
    channel: 'VOICE',
    messagePurpose: 'MARKETING',
    b2bOrB2c: null,
    requiredChecks: [
      { checkId: 'CHK_NATIONAL_REGISTRY_CLEAR', required: true, params: { registry: 'AR_NO_LLAME' } },
    ],
    effect: 'BLOCK',
    remediation: '{ "code": "REMED_REMOVE_FROM_CALL_LIST", "registry": "AR_NO_LLAME" }',
    sourceRef: 'Telcom Laws: Part III §8.1 "No Llame"',
    priority: 140,
    isActive: true,
    isOverridable: false,
    isSystem: true,
    createdAt: d,
    updatedAt: d,
  },

  // -----------------------------------------------
  // Chile
  // -----------------------------------------------
  {
    id: '20000000-0000-0000-0000-000000000040',
    ruleId: 'PRE-CL-NOMOLESTAR-001',
    name: 'Chile No Molestar Registry Block',
    description: 'Chile No Molestar platform blocks marketing contact to registered recipients across all channels.',
    jurisdictionCode: 'CL',
    layer: 'constraint',
    channel: null,
    messagePurpose: 'MARKETING',
    b2bOrB2c: null,
    requiredChecks: [
      { checkId: 'CHK_NATIONAL_REGISTRY_CLEAR', required: true, params: { registry: 'CL_NO_MOLESTAR' } },
    ],
    effect: 'BLOCK',
    remediation: 'Remove from lists immediately.',
    sourceRef: 'Telcom Laws: Part III §8.2 "No Molestar"',
    priority: 150,
    isActive: true,
    isOverridable: false,
    isSystem: true,
    createdAt: d,
    updatedAt: d,
  },
  {
    id: '20000000-0000-0000-0000-000000000041',
    ruleId: 'PRE-CL-HRS-001',
    name: 'Chile Calling Hours (8am-8pm Mon-Sat)',
    description: 'Chile restricts promotional calls to Mon-Sat 8:00 AM – 8:00 PM. Sunday calls are prohibited.',
    jurisdictionCode: 'CL',
    layer: 'constraint',
    channel: 'VOICE',
    messagePurpose: 'MARKETING',
    b2bOrB2c: null,
    requiredChecks: [
      { checkId: 'CHK_WITHIN_CALLING_HOURS', required: true, params: { window: 'CL_PROMOTIONAL_08_20' } },
    ],
    effect: 'CONDITION',
    remediation: 'Defer the send to the next Chilean promotional window (8:00 AM – 8:00 PM, Mon–Sat).',
    sourceRef: 'Telcom Laws: Part III §8.2 "Hours"',
    priority: 151,
    isActive: true,
    isOverridable: false,
    isSystem: true,
    createdAt: d,
    updatedAt: d,
  },

  // -----------------------------------------------
  // Peru
  // -----------------------------------------------
  {
    id: '20000000-0000-0000-0000-000000000042',
    ruleId: 'PRE-PE-CONSENT-001',
    name: 'Peru Express Consent Requirement',
    description: 'Peru requires prior, informed, express consent for marketing communications across all channels.',
    jurisdictionCode: 'PE',
    layer: 'eligibility',
    channel: null,
    messagePurpose: 'MARKETING',
    b2bOrB2c: null,
    requiredChecks: [
      { checkId: 'CHK_CONSENT_AT_LEAST', required: true, params: { minConsent: 'EXPRESS_OPTIN' } },
    ],
    effect: 'BLOCK',
    remediation: 'Obtain express opt-in consent.',
    sourceRef: 'Telcom Laws: Part III §8.3 "Consent"',
    priority: 160,
    isActive: true,
    isOverridable: false,
    isSystem: true,
    createdAt: d,
    updatedAt: d,
  },
  {
    id: '20000000-0000-0000-0000-000000000043',
    ruleId: 'PRE-PE-HRS-001',
    name: 'Peru Right to Disconnect',
    description: 'Peru right-to-disconnect laws restrict outreach to business hours.',
    jurisdictionCode: 'PE',
    layer: 'constraint',
    channel: 'VOICE',
    messagePurpose: 'MARKETING',
    b2bOrB2c: null,
    requiredChecks: [
      { checkId: 'CHK_RIGHT_TO_DISCONNECT_OK', required: true, params: { businessHoursOnly: true, ifUnknown: 'riskFlag' } },
    ],
    effect: 'CONDITION',
    remediation: 'Restrict outreach to business hours; attach risk flag if unknown.',
    sourceRef: 'Telcom Laws: Part III §8.3 "Right to Disconnect"',
    priority: 161,
    isActive: true,
    isOverridable: false,
    isSystem: true,
    createdAt: d,
    updatedAt: d,
  },

  // -----------------------------------------------
  // SMS calling hours — activate the seeded SMS windows.
  // The federal TCPA/TSR window and the FL/WA/MD/TX statutes cover telephonic
  // sales *texts*, not just voice. Without these rules the SMS windows are
  // seeded but never consulted (no rule fires CHK_WITHIN_CALLING_HOURS for
  // SMS). Effect CONDITION → an out-of-hours text DEFERS to the next window.
  // -----------------------------------------------
  {
    id: '20000000-0000-0000-0000-000000000050',
    ruleId: 'PRE-US-HRS-SMS-001',
    name: 'US Federal Calling Hours (SMS)',
    description: 'Marketing text messages restricted to 8:00 AM – 9:00 PM recipient-local time.',
    jurisdictionCode: 'US-FED',
    layer: 'constraint',
    channel: 'SMS',
    messagePurpose: 'MARKETING',
    b2bOrB2c: null,
    requiredChecks: [
      { checkId: 'CHK_WITHIN_CALLING_HOURS', required: true, params: { window: 'US_FED_TELEMARKETING_SMS_08_21' } },
    ],
    effect: 'CONDITION',
    remediation: 'Defer the send to the next US federal telemarketing window (8:00 AM – 9:00 PM recipient-local).',
    sourceRef: 'Telcom Laws: Part I "Table 1" (Federal row) — applies to marketing texts',
    priority: 23,
    isActive: true,
    isOverridable: false,
    isSystem: true,
    createdAt: d,
    updatedAt: d,
  },
  {
    id: '20000000-0000-0000-0000-000000000051',
    ruleId: 'PRE-US-FL-HRS-SMS-001',
    name: 'Florida Calling Hours (SMS)',
    description: 'FTSA restricts marketing text messages to 8:00 AM – 8:00 PM local time for FL area codes.',
    jurisdictionCode: 'US-FL',
    layer: 'constraint',
    channel: 'SMS',
    messagePurpose: 'MARKETING',
    b2bOrB2c: null,
    requiredChecks: [
      { checkId: 'CHK_WITHIN_CALLING_HOURS', required: true, params: { window: 'US_FL_SMS_08_20' } },
    ],
    effect: 'CONDITION',
    remediation: 'Defer the send to the next Florida calling window (8:00 AM – 8:00 PM recipient-local).',
    sourceRef: 'Telcom Laws: Part I §2.1 "Calling Hours" (covers texts)',
    priority: 33,
    isActive: true,
    isOverridable: false,
    isSystem: true,
    createdAt: d,
    updatedAt: d,
  },
  {
    id: '20000000-0000-0000-0000-000000000052',
    ruleId: 'PRE-US-WA-HRS-SMS-001',
    name: 'Washington Calling Hours (SMS)',
    description: 'Washington CSTA restricts marketing text messages to 8:00 AM – 8:00 PM local time.',
    jurisdictionCode: 'US-WA',
    layer: 'constraint',
    channel: 'SMS',
    messagePurpose: 'MARKETING',
    b2bOrB2c: null,
    requiredChecks: [
      { checkId: 'CHK_WITHIN_CALLING_HOURS', required: true, params: { window: 'US_WA_SMS_08_20' } },
    ],
    effect: 'CONDITION',
    remediation: 'Defer the send to the next Washington calling window (8:00 AM – 8:00 PM recipient-local).',
    sourceRef: 'Telcom Laws: Part I §2.3 "Calling Hours" (covers texts)',
    priority: 53,
    isActive: true,
    isOverridable: false,
    isSystem: true,
    createdAt: d,
    updatedAt: d,
  },
  {
    id: '20000000-0000-0000-0000-000000000053',
    ruleId: 'PRE-US-MD-HRS-SMS-001',
    name: 'Maryland Calling Hours (SMS)',
    description: 'Maryland Stop the Spam Calls Act restricts marketing text messages to 8:00 AM – 8:00 PM local time.',
    jurisdictionCode: 'US-MD',
    layer: 'constraint',
    channel: 'SMS',
    messagePurpose: 'MARKETING',
    b2bOrB2c: null,
    requiredChecks: [
      { checkId: 'CHK_WITHIN_CALLING_HOURS', required: true, params: { window: 'US_MD_SMS_08_20' } },
    ],
    effect: 'CONDITION',
    remediation: 'Defer the send to the next Maryland calling window (8:00 AM – 8:00 PM recipient-local).',
    sourceRef: 'Telcom Laws: Part I §2.4 "Frequency and Hours" (covers texts)',
    priority: 63,
    isActive: true,
    isOverridable: false,
    isSystem: true,
    createdAt: d,
    updatedAt: d,
  },
  {
    id: '20000000-0000-0000-0000-000000000054',
    ruleId: 'PRE-US-TX-HRS-SMS-001',
    name: 'Texas Calling Hours (SMS)',
    description: 'TX Business & Commerce Code §305.053 restricts marketing text messages to 9:00 AM – 9:00 PM local time (stricter than the federal window).',
    jurisdictionCode: 'US-TX',
    layer: 'constraint',
    channel: 'SMS',
    messagePurpose: 'MARKETING',
    b2bOrB2c: null,
    requiredChecks: [
      { checkId: 'CHK_WITHIN_CALLING_HOURS', required: true, params: { window: 'US_TX_SMS_09_21' } },
    ],
    effect: 'CONDITION',
    remediation: 'Defer the send to the next Texas calling window (9:00 AM – 9:00 PM recipient-local).',
    sourceRef: 'TX Business & Commerce Code §305.053 (covers texts)',
    priority: 71,
    isActive: true,
    isOverridable: false,
    isSystem: true,
    createdAt: d,
    updatedAt: d,
  },

  // -----------------------------------------------
  // Texas voice — activate the seeded TX voice window (was inert: no TX rule
  // existed for either channel). §305.053 is 9am-9pm, stricter than federal.
  // -----------------------------------------------
  {
    id: '20000000-0000-0000-0000-000000000055',
    ruleId: 'PRE-US-TX-HRS-001',
    name: 'Texas Calling Hours (Voice)',
    description: 'TX Business & Commerce Code §305.053 restricts marketing voice calls to 9:00 AM – 9:00 PM local time (stricter than the federal 8am-9pm window).',
    jurisdictionCode: 'US-TX',
    layer: 'constraint',
    channel: 'VOICE',
    messagePurpose: 'MARKETING',
    b2bOrB2c: null,
    requiredChecks: [
      { checkId: 'CHK_WITHIN_CALLING_HOURS', required: true, params: { window: 'US_TX_09_21' } },
    ],
    effect: 'CONDITION',
    remediation: 'Defer the send to the next Texas calling window (9:00 AM – 9:00 PM recipient-local).',
    sourceRef: 'TX Business & Commerce Code §305.053',
    priority: 70,
    isActive: true,
    isOverridable: false,
    isSystem: true,
    createdAt: d,
    updatedAt: d,
  },
];

// ============================================
// Calling Hour Windows (19 entries — 12 VOICE + 6 SMS + Texas voice)
// ============================================

export const PRE_CALLING_HOUR_WINDOWS: CallingHourWindow[] = [
  {
    id: '30000000-0000-0000-0000-000000000001',
    windowId: 'US_FED_TELEMARKETING_08_21',
    jurisdictionCode: 'US-FED',
    name: 'US Federal Telemarketing (8am-9pm)',
    channel: 'VOICE',
    messagePurpose: 'MARKETING',
    startHour: 8,
    startMinute: 0,
    endHour: 21,
    endMinute: 0,
    daysOfWeek: [0, 1, 2, 3, 4, 5, 6],
    timezone: 'recipient-local',
    sourceRef: 'TCPA / TSR Federal baseline',
    isActive: true,
    createdAt: d,
    updatedAt: d,
  },
  {
    id: '30000000-0000-0000-0000-000000000002',
    windowId: 'US_FL_08_20',
    jurisdictionCode: 'US-FL',
    name: 'Florida FTSA (8am-8pm)',
    channel: 'VOICE',
    messagePurpose: 'MARKETING',
    startHour: 8,
    startMinute: 0,
    endHour: 20,
    endMinute: 0,
    daysOfWeek: [0, 1, 2, 3, 4, 5, 6],
    timezone: 'recipient-local',
    sourceRef: 'Florida Telephone Solicitation Act',
    isActive: true,
    createdAt: d,
    updatedAt: d,
  },
  {
    id: '30000000-0000-0000-0000-000000000003',
    windowId: 'US_OK_08_20',
    jurisdictionCode: 'US-OK',
    name: 'Oklahoma (8am-8pm)',
    channel: 'VOICE',
    messagePurpose: 'MARKETING',
    startHour: 8,
    startMinute: 0,
    endHour: 20,
    endMinute: 0,
    daysOfWeek: [0, 1, 2, 3, 4, 5, 6],
    timezone: 'recipient-local',
    sourceRef: 'Oklahoma Telephone Solicitation Act of 2022',
    isActive: true,
    createdAt: d,
    updatedAt: d,
  },
  {
    id: '30000000-0000-0000-0000-000000000004',
    windowId: 'US_WA_08_20',
    jurisdictionCode: 'US-WA',
    name: 'Washington CSTA (8am-8pm)',
    channel: 'VOICE',
    messagePurpose: 'MARKETING',
    startHour: 8,
    startMinute: 0,
    endHour: 20,
    endMinute: 0,
    daysOfWeek: [0, 1, 2, 3, 4, 5, 6],
    timezone: 'recipient-local',
    sourceRef: 'Washington Commercial Telephone Solicitation Act',
    isActive: true,
    createdAt: d,
    updatedAt: d,
  },
  {
    id: '30000000-0000-0000-0000-000000000005',
    windowId: 'US_MD_08_20',
    jurisdictionCode: 'US-MD',
    name: 'Maryland (8am-8pm)',
    channel: 'VOICE',
    messagePurpose: 'MARKETING',
    startHour: 8,
    startMinute: 0,
    endHour: 20,
    endMinute: 0,
    daysOfWeek: [0, 1, 2, 3, 4, 5, 6],
    timezone: 'recipient-local',
    sourceRef: 'Maryland Stop the Spam Calls Act',
    isActive: true,
    createdAt: d,
    updatedAt: d,
  },
  {
    id: '30000000-0000-0000-0000-000000000006',
    windowId: 'CA_UTR_TELEMARKETING',
    jurisdictionCode: 'CA',
    name: 'Canada UTR Weekday (9am-9:30pm)',
    channel: 'VOICE',
    messagePurpose: 'MARKETING',
    startHour: 9,
    startMinute: 0,
    endHour: 21,
    endMinute: 30,
    daysOfWeek: [1, 2, 3, 4, 5],
    timezone: 'recipient-local',
    sourceRef: 'Canada Unsolicited Telecommunications Rules §4.3',
    isActive: true,
    createdAt: d,
    updatedAt: d,
  },
  {
    id: '30000000-0000-0000-0000-000000000007',
    windowId: 'CA_UTR_TELEMARKETING_WEEKEND',
    jurisdictionCode: 'CA',
    name: 'Canada UTR Weekend (10am-6pm)',
    channel: 'VOICE',
    messagePurpose: 'MARKETING',
    startHour: 10,
    startMinute: 0,
    endHour: 18,
    endMinute: 0,
    daysOfWeek: [0, 6],
    timezone: 'recipient-local',
    sourceRef: 'Canada Unsolicited Telecommunications Rules §4.3',
    isActive: true,
    createdAt: d,
    updatedAt: d,
  },
  {
    id: '30000000-0000-0000-0000-000000000008',
    windowId: 'BR_TELEMARKETING',
    jurisdictionCode: 'BR',
    name: 'Brazil Telemarketing (8am-8pm weekday, 8am-2pm Sat)',
    channel: 'VOICE',
    messagePurpose: 'MARKETING',
    startHour: 8,
    startMinute: 0,
    endHour: 20,
    endMinute: 0,
    daysOfWeek: [1, 2, 3, 4, 5],
    timezone: 'recipient-local',
    sourceRef: 'Brazil state laws (e.g., São Paulo Law No. 13.226)',
    isActive: true,
    createdAt: d,
    updatedAt: d,
  },
  {
    id: '30000000-0000-0000-0000-000000000009',
    windowId: 'MX_TELEMARKETING',
    jurisdictionCode: 'MX',
    name: 'Mexico Telemarketing (8am-7pm Mon-Fri)',
    channel: 'VOICE',
    messagePurpose: 'MARKETING',
    startHour: 8,
    startMinute: 0,
    endHour: 19,
    endMinute: 0,
    daysOfWeek: [1, 2, 3, 4, 5],
    timezone: 'recipient-local',
    sourceRef: 'Mexico PROFECO consumer protection regulations',
    isActive: true,
    createdAt: d,
    updatedAt: d,
  },
  {
    id: '30000000-0000-0000-0000-000000000010',
    windowId: 'CO_STRICT_CONTACT_HOURS',
    jurisdictionCode: 'CO',
    name: 'Colombia Strict Hours (7am-7pm M-F, 8am-3pm Sat)',
    channel: 'VOICE',
    messagePurpose: 'MARKETING',
    startHour: 7,
    startMinute: 0,
    endHour: 19,
    endMinute: 0,
    daysOfWeek: [1, 2, 3, 4, 5],
    timezone: 'recipient-local',
    sourceRef: 'Colombia Law 2300 "Dejen de Fregar"',
    isActive: true,
    createdAt: d,
    updatedAt: d,
  },
  {
    id: '30000000-0000-0000-0000-000000000011',
    windowId: 'CL_PROMOTIONAL_08_20',
    jurisdictionCode: 'CL',
    name: 'Chile Promotional (8am-8pm Mon-Sat)',
    channel: 'VOICE',
    messagePurpose: 'MARKETING',
    startHour: 8,
    startMinute: 0,
    endHour: 20,
    endMinute: 0,
    daysOfWeek: [1, 2, 3, 4, 5, 6],
    timezone: 'recipient-local',
    sourceRef: 'Chile SERNAC regulations',
    isActive: true,
    createdAt: d,
    updatedAt: d,
  },
  {
    id: '30000000-0000-0000-0000-000000000012',
    windowId: 'PE_BUSINESS_HOURS',
    jurisdictionCode: 'PE',
    name: 'Peru Business Hours (8am-6pm Mon-Fri)',
    channel: 'VOICE',
    messagePurpose: 'MARKETING',
    startHour: 8,
    startMinute: 0,
    endHour: 18,
    endMinute: 0,
    daysOfWeek: [1, 2, 3, 4, 5],
    timezone: 'recipient-local',
    sourceRef: 'Peru Code of Consumer Protection / Right to Disconnect',
    isActive: true,
    createdAt: d,
    updatedAt: d,
  },

  // --- SMS calling-hours coverage ---------------------------------------------
  // The TCPA/TSR window and the FL/WA/MD "mini-TCPA" statutes cover telephonic
  // sales *texts*, not just voice calls, so the SMS channel needs its own
  // windows (the original windows are VOICE-only). Mirrors the voice windows.
  // NOTE: legal to confirm exact statutory hours before relying on these.
  {
    id: '30000000-0000-0000-0000-0000000000a1',
    windowId: 'US_FED_TELEMARKETING_SMS_08_21',
    jurisdictionCode: 'US-FED',
    name: 'US Federal Telemarketing — SMS (8am-9pm)',
    channel: 'SMS',
    messagePurpose: 'MARKETING',
    startHour: 8,
    startMinute: 0,
    endHour: 21,
    endMinute: 0,
    daysOfWeek: [0, 1, 2, 3, 4, 5, 6],
    timezone: 'recipient-local',
    sourceRef: 'TCPA / TSR Federal baseline (applies to marketing texts)',
    isActive: true,
    createdAt: d,
    updatedAt: d,
  },
  {
    id: '30000000-0000-0000-0000-0000000000a2',
    windowId: 'US_FL_SMS_08_20',
    jurisdictionCode: 'US-FL',
    name: 'Florida FTSA — SMS (8am-8pm)',
    channel: 'SMS',
    messagePurpose: 'MARKETING',
    startHour: 8,
    startMinute: 0,
    endHour: 20,
    endMinute: 0,
    daysOfWeek: [0, 1, 2, 3, 4, 5, 6],
    timezone: 'recipient-local',
    sourceRef: 'Florida Telephone Solicitation Act (covers text messages)',
    isActive: true,
    createdAt: d,
    updatedAt: d,
  },
  {
    id: '30000000-0000-0000-0000-0000000000a7',
    windowId: 'US_OK_SMS_08_20',
    jurisdictionCode: 'US-OK',
    name: 'Oklahoma — SMS (8am-8pm)',
    channel: 'SMS',
    messagePurpose: 'MARKETING',
    startHour: 8,
    startMinute: 0,
    endHour: 20,
    endMinute: 0,
    daysOfWeek: [0, 1, 2, 3, 4, 5, 6],
    timezone: 'recipient-local',
    sourceRef: 'Oklahoma Telephone Solicitation Act of 2022 (covers texts)',
    isActive: true,
    createdAt: d,
    updatedAt: d,
  },
  {
    id: '30000000-0000-0000-0000-0000000000a3',
    windowId: 'US_WA_SMS_08_20',
    jurisdictionCode: 'US-WA',
    name: 'Washington CSTA — SMS (8am-8pm)',
    channel: 'SMS',
    messagePurpose: 'MARKETING',
    startHour: 8,
    startMinute: 0,
    endHour: 20,
    endMinute: 0,
    daysOfWeek: [0, 1, 2, 3, 4, 5, 6],
    timezone: 'recipient-local',
    sourceRef: 'Washington Commercial Telephone Solicitation Act (covers texts)',
    isActive: true,
    createdAt: d,
    updatedAt: d,
  },
  {
    id: '30000000-0000-0000-0000-0000000000a4',
    windowId: 'US_MD_SMS_08_20',
    jurisdictionCode: 'US-MD',
    name: 'Maryland — SMS (8am-8pm)',
    channel: 'SMS',
    messagePurpose: 'MARKETING',
    startHour: 8,
    startMinute: 0,
    endHour: 20,
    endMinute: 0,
    daysOfWeek: [0, 1, 2, 3, 4, 5, 6],
    timezone: 'recipient-local',
    sourceRef: 'Maryland Stop the Spam Calls Act (covers texts)',
    isActive: true,
    createdAt: d,
    updatedAt: d,
  },

  // --- Texas (TX Business & Commerce Code §305.053): 9am-9pm, stricter than the
  // federal 8am-9pm window. Applies to voice and texts. -----------------------
  {
    id: '30000000-0000-0000-0000-0000000000a5',
    windowId: 'US_TX_09_21',
    jurisdictionCode: 'US-TX',
    name: 'Texas Telemarketing — Voice (9am-9pm)',
    channel: 'VOICE',
    messagePurpose: 'MARKETING',
    startHour: 9,
    startMinute: 0,
    endHour: 21,
    endMinute: 0,
    daysOfWeek: [0, 1, 2, 3, 4, 5, 6],
    timezone: 'recipient-local',
    sourceRef: 'TX Business & Commerce Code §305.053',
    isActive: true,
    createdAt: d,
    updatedAt: d,
  },
  {
    id: '30000000-0000-0000-0000-0000000000a6',
    windowId: 'US_TX_SMS_09_21',
    jurisdictionCode: 'US-TX',
    name: 'Texas Telemarketing — SMS (9am-9pm)',
    channel: 'SMS',
    messagePurpose: 'MARKETING',
    startHour: 9,
    startMinute: 0,
    endHour: 21,
    endMinute: 0,
    daysOfWeek: [0, 1, 2, 3, 4, 5, 6],
    timezone: 'recipient-local',
    sourceRef: 'TX Business & Commerce Code §305.053 (covers texts)',
    isActive: true,
    createdAt: d,
    updatedAt: d,
  },
];

// ============================================
// DNC Registries (6 entries)
// ============================================

export const PRE_DNC_REGISTRIES: DNCRegistry[] = [
  {
    id: '40000000-0000-0000-0000-000000000001',
    registryId: 'US_DNC',
    name: 'US National Do Not Call Registry',
    country: 'US',
    jurisdictionCode: 'US-FED',
    registryType: 'NATIONAL',
    scrubCadenceDays: 31,
    channel: 'VOICE',
    description: 'FTC/FCC National DNC Registry. Scrub every 31 days for B2C marketing voice calls.',
    isActive: true,
    createdAt: d,
    updatedAt: d,
  },
  {
    id: '40000000-0000-0000-0000-000000000002',
    registryId: 'CA_DNCL',
    name: 'Canada National DNCL',
    country: 'CA',
    jurisdictionCode: 'CA',
    registryType: 'NATIONAL',
    scrubCadenceDays: 31,
    channel: 'VOICE',
    description: 'CRTC National Do Not Call List. B2B calls exempt. Scrub every 31 days for B2C voice.',
    isActive: true,
    createdAt: d,
    updatedAt: d,
  },
  {
    id: '40000000-0000-0000-0000-000000000003',
    registryId: 'MX_REPEP',
    name: 'Mexico REPEP Registry',
    country: 'MX',
    jurisdictionCode: 'MX',
    registryType: 'NATIONAL',
    scrubCadenceDays: 30,
    channel: 'VOICE',
    description: 'PROFECO Registro Público para Evitar Publicidad. Scrub every 30 days.',
    isActive: true,
    createdAt: d,
    updatedAt: d,
  },
  {
    id: '40000000-0000-0000-0000-000000000007',
    registryId: 'MX_REUS',
    name: 'Mexico CONDUSEF REUS',
    country: 'MX',
    jurisdictionCode: 'MX',
    registryType: 'NATIONAL',
    scrubCadenceDays: 30,
    channel: null,
    description: 'CONDUSEF Registro Público de Usuarios. Mandatory consultation for financial-sector marketers (banks, insurance, investment, trust funds) before promotional outreach. Separate from PROFECO REPEP.',
    isActive: true,
    createdAt: d,
    updatedAt: d,
  },
  {
    id: '40000000-0000-0000-0000-000000000004',
    registryId: 'AR_NO_LLAME',
    name: 'Argentina No Llame Registry',
    country: 'AR',
    jurisdictionCode: 'AR',
    registryType: 'NATIONAL',
    scrubCadenceDays: 30,
    channel: 'VOICE',
    description: 'National No Llame registry (Law 26.951). Protects landline and mobile users.',
    isActive: true,
    createdAt: d,
    updatedAt: d,
  },
  {
    id: '40000000-0000-0000-0000-000000000005',
    registryId: 'CL_NO_MOLESTAR',
    name: 'Chile No Molestar Platform',
    country: 'CL',
    jurisdictionCode: 'CL',
    registryType: 'NATIONAL',
    scrubCadenceDays: 30,
    channel: null,
    description: 'SERNAC No Molestar platform. Acts as instant block across all channels.',
    isActive: true,
    createdAt: d,
    updatedAt: d,
  },
  {
    id: '40000000-0000-0000-0000-000000000006',
    registryId: 'BR_NAO_ME_PERTURBE',
    name: 'Brazil Não Me Perturbe',
    country: 'BR',
    jurisdictionCode: 'BR',
    registryType: 'NATIONAL',
    scrubCadenceDays: 30,
    channel: 'VOICE',
    description: 'National Não Me Perturbe registry. Applies primarily to telecom and finance sectors.',
    isActive: true,
    createdAt: d,
    updatedAt: d,
  },
];

// ============================================
// Frequency Cap Configurations (4 entries)
// ============================================

export const PRE_FREQUENCY_CAPS: FrequencyCapConfig[] = [
  {
    id: '50000000-0000-0000-0000-000000000001',
    jurisdictionCode: 'US-FL',
    name: 'Florida 3/24h Cap',
    channel: null,
    messagePurpose: 'MARKETING',
    maxAttempts: 3,
    periodHours: 24,
    crossChannel: false,
    sourceRef: 'Florida Telephone Solicitation Act §2.1',
    isActive: true,
    createdAt: d,
    updatedAt: d,
  },
  {
    id: '50000000-0000-0000-0000-000000000002',
    jurisdictionCode: 'US-OK',
    name: 'Oklahoma 3/24h Cap',
    channel: null,
    messagePurpose: 'MARKETING',
    maxAttempts: 3,
    periodHours: 24,
    crossChannel: false,
    sourceRef: 'Oklahoma Telephone Solicitation Act of 2022 §2.2',
    isActive: true,
    createdAt: d,
    updatedAt: d,
  },
  {
    id: '50000000-0000-0000-0000-000000000003',
    jurisdictionCode: 'US-MD',
    name: 'Maryland 3/24h Cap',
    channel: null,
    messagePurpose: 'MARKETING',
    maxAttempts: 3,
    periodHours: 24,
    crossChannel: false,
    sourceRef: 'Maryland Stop the Spam Calls Act §2.4',
    isActive: true,
    createdAt: d,
    updatedAt: d,
  },
  {
    id: '50000000-0000-0000-0000-000000000004',
    jurisdictionCode: 'CO',
    name: 'Colombia 1/7d Cross-Channel Cap',
    channel: null,
    messagePurpose: 'MARKETING',
    maxAttempts: 1,
    periodHours: 168,
    crossChannel: true,
    sourceRef: 'Colombia Law 2300 §7.1',
    isActive: true,
    createdAt: d,
    updatedAt: d,
  },
];

// ============================================
// Check Definitions (21 entries)
// ============================================

export const PRE_CHECK_DEFINITIONS: PRECheckDefinition[] = [
  { checkId: 'CHK_INTERNAL_DNC_CLEAR', name: 'Internal DNC Clear', description: 'Verifies the recipient is not on the internal do-not-contact list.', paramSchema: null, userFacingTemplate: 'Recipient is not on your internal do-not-contact list.', ruleType: 'OPT_OUT' },
  { checkId: 'CHK_NATIONAL_REGISTRY_SCRUBBED', name: 'National Registry Scrubbed', description: 'Verifies the national DNC/registry scrub is current (within cadence).', paramSchema: { registry: 'string', maxAgeDays: 'number' }, userFacingTemplate: 'Your {registry} registry scrub is current (refreshed within the last {maxAgeDays} days).', ruleType: 'SENDING' },
  { checkId: 'CHK_NATIONAL_REGISTRY_CLEAR', name: 'National Registry Clear', description: 'Verifies the recipient is not on the applicable national registry.', paramSchema: { registry: 'string' }, userFacingTemplate: 'Recipient is not listed on the {registry} registry.', ruleType: 'OPT_OUT' },
  { checkId: 'CHK_WITHIN_CALLING_HOURS', name: 'Within Calling Hours', description: 'Verifies the recipient-local time is within the configured calling window.', paramSchema: { window: 'string' }, userFacingTemplate: 'Send is within the {window} time window in the recipient\u2019s local timezone.', ruleType: 'SENDING' },
  { checkId: 'CHK_FREQUENCY_CAP_24H_OK', name: 'Frequency Cap (24h)', description: 'Verifies attempts in last 24h for the same subject do not exceed the cap.', paramSchema: { cap: 'number', subjectKey: 'string' }, userFacingTemplate: 'No more than {cap} attempts in any 24-hour window per {subjectKey}.', ruleType: 'SENDING' },
  { checkId: 'CHK_FREQUENCY_CAP_7D_OK', name: 'Frequency Cap (7d)', description: 'Verifies contacts in last 7 days for the same subject do not exceed the cap (optionally cross-channel).', paramSchema: { cap: 'number', subjectKey: 'string', crossChannel: 'boolean' }, userFacingTemplate: 'No more than {cap} contacts in any 7-day window per {subjectKey}.', ruleType: 'SENDING' },
  { checkId: 'CHK_CHANNEL_PREFERENCE_MATCH', name: 'Channel Preference Match', description: 'If the recipient has a preferred channel set, verifies the requested channel matches it.', paramSchema: { ifPreferredChannelSet: 'string' }, userFacingTemplate: 'If the recipient has set a preferred channel, this send uses that channel.', ruleType: 'OPT_IN' },
  { checkId: 'CHK_CONSENT_AT_LEAST', name: 'Consent At Least', description: 'Verifies the consent status meets or exceeds the required minimum level.', paramSchema: { minConsent: 'string' }, userFacingTemplate: 'Recipient consent is at least {minConsent}.', ruleType: 'OPT_IN' },
  { checkId: 'CHK_CONTACT_ELIGIBLE', name: 'Contact Eligible', description: 'Verifies the contact has eligibility (green flag) for the requested channel \u00d7 purpose.', paramSchema: null, userFacingTemplate: 'Recipient is marked eligible for this channel and purpose.', ruleType: 'OPT_IN' },
  { checkId: 'CHK_SENDER_ID_AND_UNSUB_PRESENT', name: 'Sender ID & Unsubscribe Present', description: 'Verifies the message includes required sender identification and unsubscribe mechanism.', paramSchema: { unsubscribeMaxDays: 'number' }, userFacingTemplate: 'Message includes sender identification and a working unsubscribe option (honored within {unsubscribeMaxDays} days).', ruleType: 'OPT_OUT' },
  { checkId: 'CHK_BRAZIL_0303_PREFIX', name: 'Brazil 0303 Prefix', description: 'Verifies the outbound voice caller ID uses the required 0303 prefix (Brazil).', paramSchema: { appliesToSectors: 'string' }, userFacingTemplate: 'Outbound voice caller ID uses the 0303 prefix.', ruleType: 'SENDING' },
  { checkId: 'CHK_REVOCATION_HANDLING_ENABLED', name: 'Revocation Handling Enabled', description: 'Verifies the system can process opt-out/STOP/revocation immediately.', paramSchema: null, userFacingTemplate: 'System processes opt-out and STOP requests immediately.', ruleType: 'OPT_OUT' },
  { checkId: 'CHK_WIRELESS_MARKETING_CONSENT_OK', name: 'Wireless Marketing Consent', description: 'Passes if the line is not wireless, or consent satisfies the required level for wireless marketing.', paramSchema: { ifWirelessMinConsent: 'string', ifLineTypeUnknown: 'string' }, userFacingTemplate: 'If the line is wireless, recipient has at least {ifWirelessMinConsent} consent.', ruleType: 'OPT_IN' },
  { checkId: 'CHK_AUTOMATION_ALLOWED_FOR_SEND', name: 'Automation Allowed', description: 'Verifies the dialing/selection automation mode is allowed for the jurisdiction/purpose.', paramSchema: { allowedIf: 'string' }, userFacingTemplate: 'Dialing or selection automation matches the allowed mode ({allowedIf}).', ruleType: 'SENDING' },
  { checkId: 'CHK_END_CALL_WITHIN_10S_ON_REQUEST', name: 'End Call Within 10s', description: 'Verifies the voice system enforces 10-second termination when requested (WA).', paramSchema: { maxSeconds: 'number' }, userFacingTemplate: 'Voice call can be terminated within {maxSeconds} seconds when the recipient requests it.', ruleType: 'OPT_OUT' },
  { checkId: 'CHK_DNC_DISCLOSURE_AT_CALL_START', name: 'DNC Disclosure at Call Start', description: 'Verifies the call script includes internal-DNC disclosure at the beginning (NY).', paramSchema: { placement: 'string' }, userFacingTemplate: 'Call script includes the internal-DNC disclosure at the {placement} of the call.', ruleType: 'OPT_OUT' },
  { checkId: 'CHK_CASL_CONSENT_VALID', name: 'CASL Consent Valid', description: 'Verifies CASL consent is valid (express OR valid implied window with EBR).', paramSchema: { impliedPurchaseWindowMonths: 'number', impliedInquiryWindowMonths: 'number' }, userFacingTemplate: 'Recipient has valid CASL consent: express opt-in, or implied via an existing business relationship within {impliedPurchaseWindowMonths} months (purchase) or {impliedInquiryWindowMonths} months (inquiry).', ruleType: 'OPT_IN' },
  { checkId: 'CHK_CASL_CONSPICUOUS_PUBLICATION_VALID', name: 'CASL Conspicuous Publication', description: 'Verifies the B2B conspicuous-publication exemption conditions are met with evidence.', paramSchema: { requiresEvidenceSnapshot: 'boolean', requiresRelevance: 'boolean', requiresNoDisclaimer: 'boolean' }, userFacingTemplate: 'B2B conspicuous-publication exemption conditions are met with stored evidence.', ruleType: 'OPT_IN' },
  { checkId: 'CHK_LGPD_LAWFUL_BASIS_VALID', name: 'LGPD Lawful Basis Valid', description: 'Verifies LGPD lawful basis is valid (consent OR legitimate interest with LIA).', paramSchema: { preferred: 'string', allowLegitimateInterestWithLIA: 'boolean' }, userFacingTemplate: 'Recipient has a valid LGPD lawful basis (preferred: {preferred}).', ruleType: 'OPT_IN' },
  { checkId: 'CHK_BRAZIL_NAO_ME_PERTURBE_CLEAR', name: 'N\u00e3o Me Perturbe Clear', description: 'Verifies the recipient is not blocked by the N\u00e3o Me Perturbe registry (Brazil).', paramSchema: null, userFacingTemplate: 'Recipient is not blocked by the N\u00e3o Me Perturbe registry.', ruleType: 'OPT_OUT' },
  { checkId: 'CHK_RIGHT_TO_DISCONNECT_OK', name: 'Right to Disconnect', description: 'Verifies outbound timing respects right-to-disconnect / business-hours constraint.', paramSchema: { businessHoursOnly: 'boolean', ifUnknown: 'string' }, userFacingTemplate: 'Send respects the recipient\u2019s right-to-disconnect / business-hours constraint.', ruleType: 'OPT_OUT' },
];

// ============================================
// Mock Decision Logs (3 examples from spec)
// ============================================

export const MOCK_DECISION_LOGS: PREDecisionLog[] = [
  // Example A: US-FL + Voice + Marketing at 7:30 PM → ALLOW_WITH_CONDITIONS
  {
    id: '60000000-0000-0000-0000-000000000001',
    tenantId: '00000000-0000-0000-0000-000000000001',
    contactId: 'c0000000-0000-0000-0000-000000000001',
    campaignId: 'camp0000-0000-0000-0000-000000000001',
    channel: 'VOICE',
    messagePurpose: 'MARKETING',
    jurisdictionCode: 'US-FL',
    decision: 'ALLOW_WITH_CONDITIONS',
    eligibility: 'ELIGIBLE',
    conditionCount: 3,
    riskFlagCount: 1,
    input: {
      recipient: {
        addressing: { phone: '+13055551234', email: null },
        country: 'US',
        usAreaCode: '305',
        b2bOrB2c: 'B2C',
      },
      channel: 'VOICE',
      messagePurpose: 'MARKETING',
      contactEligibility: {
        informationalSms: true,
        informationalEmail: true,
        informationalVoice: true,
        marketingSms: true,
        marketingEmail: true,
        marketingVoice: true,
      },
      internalDncStatus: 'NOT_ON_LIST',
      nationalRegistryStatus: 'NOT_ON_LIST',
      localTimeAtRecipient: new Date('2024-06-15T19:30:00'),
      contactFrequency: {
        attemptsLast24hSameSubject: 2,
        contactsLast7dSameSubjectAnyChannel: 3,
        preferredChannel: null,
      },
      tenantId: '00000000-0000-0000-0000-000000000001',
      campaignId: 'camp0000-0000-0000-0000-000000000001',
    },
    output: {
      decision: 'ALLOW_WITH_CONDITIONS',
      eligibility: 'ELIGIBLE',
      conditions: [
        { code: 'COND_SCHEDULE_WITHIN_HOURS', params: { window: 'US_FL_08_20' }, description: 'Send within Florida calling hours (8am-8pm)' },
        { code: 'COND_REGISTRY_SCRUB_REQUIRED', params: { registry: 'US_DNC', maxAgeDays: 31 }, description: 'National DNC scrub required' },
        { code: 'COND_RECORD_RETENTION', params: { retentionYears: 2 }, description: 'Maintain required records for 2 years' },
      ],
      riskFlags: [
        { code: 'RISK_STATE_ASSUMED_BY_AREA_CODE', description: 'State determined by area code 305 (FL)' },
      ],
      remediation: [],
      trace: [
        { ruleId: 'PRE-ELIG-001', ruleName: 'Global Eligibility Gate', result: 'PASS', sourceRef: null },
        { ruleId: 'PRE-US-FL-003', ruleName: 'Florida Calling Hours (8am-8pm)', result: 'PASS', sourceRef: null },
        { ruleId: 'PRE-US-FL-002', ruleName: 'Florida Frequency Cap (3/24h)', result: 'PASS', sourceRef: null },
        { ruleId: 'PRE-GEN-003', ruleName: 'National Registry Scrub Freshness', result: 'PASS', sourceRef: null },
        { ruleId: 'PRE-GEN-004', ruleName: 'Record Retention Requirement', result: 'PASS', sourceRef: null },
      ],
      evaluatedAt: new Date('2024-06-15T19:30:05'),
    },
    createdAt: new Date('2024-06-15T19:30:05'),
  },

  // Example B: Canada + Email + B2B Marketing (conspicuous publication) → ALLOW_WITH_CONDITIONS
  {
    id: '60000000-0000-0000-0000-000000000002',
    tenantId: '00000000-0000-0000-0000-000000000001',
    contactId: 'c0000000-0000-0000-0000-000000000002',
    campaignId: 'camp0000-0000-0000-0000-000000000002',
    channel: 'EMAIL',
    messagePurpose: 'MARKETING',
    jurisdictionCode: 'CA',
    decision: 'ALLOW_WITH_CONDITIONS',
    eligibility: 'ELIGIBLE',
    conditionCount: 3,
    riskFlagCount: 0,
    input: {
      recipient: {
        addressing: { phone: null, email: 'contact@canadabiz.ca' },
        country: 'CA',
        usAreaCode: null,
        b2bOrB2c: 'B2B',
      },
      channel: 'EMAIL',
      messagePurpose: 'MARKETING',
      contactEligibility: {
        informationalSms: false,
        informationalEmail: true,
        informationalVoice: false,
        marketingSms: false,
        marketingEmail: true,
        marketingVoice: false,
      },
      internalDncStatus: 'NOT_ON_LIST',
      nationalRegistryStatus: 'NOT_ON_LIST',
      localTimeAtRecipient: new Date('2024-06-15T10:00:00'),
      contactFrequency: {
        attemptsLast24hSameSubject: 0,
        contactsLast7dSameSubjectAnyChannel: 0,
        preferredChannel: null,
      },
      tenantId: '00000000-0000-0000-0000-000000000001',
      campaignId: 'camp0000-0000-0000-0000-000000000002',
    },
    output: {
      decision: 'ALLOW_WITH_CONDITIONS',
      eligibility: 'ELIGIBLE',
      conditions: [
        { code: 'COND_CAPTURE_EVIDENCE_SNAPSHOT', params: { artifactType: 'PUBLISHED_ADDRESS' }, description: 'Capture evidence snapshot of published address' },
        { code: 'COND_VERIFY_RELEVANCE_RATIONALE', params: { required: true }, description: 'Verify message relevance to recipient role' },
        { code: 'COND_INCLUDE_SENDER_ID_AND_UNSUB', params: { unsubscribeMaxDays: 10 }, description: 'Include sender identification and unsubscribe mechanism' },
      ],
      riskFlags: [],
      remediation: [],
      trace: [
        { ruleId: 'PRE-ELIG-001', ruleName: 'Global Eligibility Gate', result: 'PASS', sourceRef: null },
        { ruleId: 'PRE-CA-CASL-003', ruleName: 'CASL Conspicuous Publication (B2B)', result: 'PASS', sourceRef: null },
        { ruleId: 'PRE-CA-CASL-002', ruleName: 'CASL Content & Form Requirements', result: 'PASS', sourceRef: null },
      ],
      evaluatedAt: new Date('2024-06-15T10:00:02'),
    },
    createdAt: new Date('2024-06-15T10:00:02'),
  },

  // Example C: Colombia + SMS + Marketing (prior email 3 days ago) → BLOCK
  {
    id: '60000000-0000-0000-0000-000000000003',
    tenantId: '00000000-0000-0000-0000-000000000001',
    contactId: 'c0000000-0000-0000-0000-000000000003',
    campaignId: 'camp0000-0000-0000-0000-000000000003',
    channel: 'SMS',
    messagePurpose: 'MARKETING',
    jurisdictionCode: 'CO',
    decision: 'BLOCK',
    eligibility: 'ELIGIBLE',
    conditionCount: 0,
    riskFlagCount: 0,
    input: {
      recipient: {
        addressing: { phone: '+573001234567', email: null },
        country: 'CO',
        usAreaCode: null,
        b2bOrB2c: 'B2C',
      },
      channel: 'SMS',
      messagePurpose: 'MARKETING',
      contactEligibility: {
        informationalSms: true,
        informationalEmail: true,
        informationalVoice: true,
        marketingSms: true,
        marketingEmail: true,
        marketingVoice: true,
      },
      internalDncStatus: 'NOT_ON_LIST',
      nationalRegistryStatus: 'NOT_ON_LIST',
      localTimeAtRecipient: new Date('2024-06-15T14:00:00'),
      contactFrequency: {
        attemptsLast24hSameSubject: 0,
        contactsLast7dSameSubjectAnyChannel: 1,
        preferredChannel: null,
      },
      tenantId: '00000000-0000-0000-0000-000000000001',
      campaignId: 'camp0000-0000-0000-0000-000000000003',
    },
    output: {
      decision: 'BLOCK',
      eligibility: 'ELIGIBLE',
      conditions: [],
      riskFlags: [],
      remediation: [
        { code: 'REMED_RETRY_AFTER_WINDOW_RESETS', params: { windowDays: 7, cap: 1, crossChannel: true }, description: 'Retry after 7-day cross-channel window resets' },
      ],
      trace: [
        { ruleId: 'PRE-ELIG-001', ruleName: 'Global Eligibility Gate', result: 'PASS', sourceRef: null },
        { ruleId: 'PRE-CO-FREQ-001', ruleName: 'Colombia Cross-Channel Frequency Cap (1/7d)', result: 'FAIL', sourceRef: null },
      ],
      evaluatedAt: new Date('2024-06-15T14:00:01'),
    },
    createdAt: new Date('2024-06-15T14:00:01'),
  },
];

// ============================================
// Mock AI Classifications (3 entries)
// ============================================

export const MOCK_AI_CLASSIFICATIONS: PREAiClassification[] = [
  {
    id: '70000000-0000-0000-0000-000000000001',
    tenantId: '00000000-0000-0000-0000-000000000001',
    messageContent: 'Don\'t miss our biggest sale of the year! Get 50% off all products this weekend only. Use code SAVE50 at checkout.',
    classifiedPurpose: 'MARKETING',
    confidence: 0.95,
    reasoning: 'Message contains promotional language ("biggest sale", "50% off"), urgency ("this weekend only"), and a promotional code. Clear marketing intent.',
    isFlagged: false,
    overriddenPurpose: null,
    overriddenBy: null,
    overriddenAt: null,
    createdAt: new Date('2024-06-15T09:00:00'),
  },
  {
    id: '70000000-0000-0000-0000-000000000002',
    tenantId: '00000000-0000-0000-0000-000000000001',
    messageContent: 'Your appointment with Dr. Smith is confirmed for Tuesday, June 18 at 2:00 PM. Please reply CONFIRM to confirm or CANCEL to cancel.',
    classifiedPurpose: 'INFORMATIONAL',
    confidence: 0.88,
    reasoning: 'Message is a transactional appointment confirmation with no promotional content. Informational/operational purpose.',
    isFlagged: false,
    overriddenPurpose: null,
    overriddenBy: null,
    overriddenAt: null,
    createdAt: new Date('2024-06-15T09:30:00'),
  },
  {
    id: '70000000-0000-0000-0000-000000000003',
    tenantId: '00000000-0000-0000-0000-000000000001',
    messageContent: 'Hi! We noticed you haven\'t logged into your account in a while. We\'ve added new features you might find useful. Check them out at app.example.com/whats-new',
    classifiedPurpose: 'INFORMATIONAL',
    confidence: 0.52,
    reasoning: 'Message appears informational (account activity notification) but contains promotional elements ("new features", link to marketing page). Low confidence.',
    isFlagged: true,
    overriddenPurpose: 'MARKETING',
    overriddenBy: '00000000-0000-0000-0000-000000000099',
    overriddenAt: new Date('2024-06-15T11:00:00'),
    createdAt: new Date('2024-06-15T10:00:00'),
  },
];
